// frontend/src/pages/Map.tsx
// COMBINED VERSION: Geographic Discovery + Portfolio Analysis
// Best features from both Dec 8 and Dec 9 versions

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container, Box, Typography, Button, Paper, Grid, Card, CardContent, TextField,
  Chip, IconButton, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Slider, Switch, FormControlLabel, List,
  ListItem, ListItemText, ListItemIcon, Menu, MenuItem, Divider, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Badge, Stack, Tabs, Tab,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Save as SaveIcon, Map as MapIcon, LocationCity as LocationCityIcon,
  Search as SearchIcon, Close as CloseIcon, Layers as LayersIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon, Clear as ClearIcon, Build as BuildIcon,
  LocationOn as LocationOnIcon, Info as InfoIcon, ArrowBack as ArrowBackIcon,
  FolderOpen as FolderOpenIcon, PlayArrow as PlayArrowIcon, FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon, CheckCircle as CheckCircleIcon, Bookmark as BookmarkIcon,
  WaterDrop as WaterDropIcon, LocalFireDepartment as FireIcon,
  Storm as StormIcon, Thermostat as HeatIcon, Landscape as EarthquakeIcon,
  Air as WindIcon, AcUnit as ColdIcon, Bolt as LightningIcon,
  Grain as DroughtIcon, Warning as WarningIcon, Compare as CompareIcon,
  Timeline as TimelineIcon, Tune as TuneIcon, Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapData, clearMapData } from '../utils/portfolioCache';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// =============== TYPE DEFINITIONS ===============

interface PortfolioItem {
  postal_code?: string;
  city?: string;
  state?: string;
  county?: string;
  latitude?: number;
  longitude?: number;
  risk_score?: number;
  asset_value?: number | string;
  // 18 Climate Hazards
  avalanche?: number;
  flood?: number;
  flood_r?: number;
  cold?: number;
  drought?: number;
  earthquake?: number;
  hail?: number;
  heat?: number;
  hurricane?: number;
  ice?: number;
  landslide?: number;
  lightning?: number;
  tornado?: number;
  tsunami?: number;
  volcanic?: number;
  wildfire?: number;
  wind?: number;
  winter?: number;
  // Environmental & Demographics
  environmental_risk_score?: number;
  superfund_sites?: number;
  epa_facilities?: number;
  toxic_air_percentile?: number;
  drinking_water_percentile?: number;
  wastewater_percentile?: number;
  pm25_percentile?: number;
  ozone_percentile?: number;
  traffic_percentile?: number;
  lead_paint_percentile?: number;
  superfund_percentile?: number;
  rmp_percentile?: number;
  tsdf_percentile?: number;
  underground_tanks_percentile?: number;
  low_income_percentile?: number;
  // Financial
  population?: number;
  building_value?: number;
  resilience?: number;
  community_risk?: number;
  [key: string]: any;
}

interface DataLayer {
  id: string;
  name: string;
  field: string;
  icon: React.ReactNode;
  color: string;
  category: 'hazard' | 'environmental' | 'demographic' | 'financial';
  scale: { min: number; max: number };
  description: string;
}

interface LayerSelection {
  layerId: string;
  visible: boolean;
  threshold: number;
  opacity: number;  // Per-layer transparency (0-1)
}

interface CityAggregate {
  city: string;
  state: string;
  postalCodes: PortfolioItem[];
  avgRisk: number;
  count: number;
  layerValues: { [field: string]: number };
}

interface SearchResult {
  postal_code: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  risk_score: number;
  search_type?: 'city' | 'postal' | 'county';
  locations?: PortfolioItem[];
}

interface FilterCondition {
  field: string;
  operator: '>=' | '<=' | '=' | '>' | '<' | 'between';
  value: number;
  value2?: number;
  enabled: boolean;
}

interface Scenario {
  id: string;
  name: string;
  items: PortfolioItem[];
  totalValue: number;
  avgRisk: number;
  propertyCount: number;
  modifications: string[];
}

interface MapNavigationState {
  mode?: 'discovery' | 'portfolio';
  loadRandomCity?: boolean;
  searchQuery?: string;
}

// =============== DATA LAYERS (37 TOTAL) ===============

const ALL_LAYERS: DataLayer[] = [
  // 18 Hazards
  { id: 'hurricane', name: 'Hurricane', field: 'hurricane', icon: <StormIcon />, color: '#9C27B0', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Hurricane wind and storm surge risk' },
  { id: 'flood', name: 'Coastal Flood', field: 'flood', icon: <WaterDropIcon />, color: '#2196F3', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Coastal flooding from tides and storms' },
  { id: 'flood_r', name: 'River Flood', field: 'flood_r', icon: <WaterDropIcon />, color: '#03A9F4', category: 'hazard', scale: { min: 0, max: 10 }, description: 'River and fluvial flooding risk' },
  { id: 'wildfire', name: 'Wildfire', field: 'wildfire', icon: <FireIcon />, color: '#FF5722', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Wildfire ignition and spread risk' },
  { id: 'earthquake', name: 'Earthquake', field: 'earthquake', icon: <EarthquakeIcon />, color: '#795548', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Seismic activity risk' },
  { id: 'drought', name: 'Drought', field: 'drought', icon: <DroughtIcon />, color: '#FFC107', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Water scarcity risk' },
  { id: 'heat', name: 'Heat Wave', field: 'heat', icon: <HeatIcon />, color: '#FF9800', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Extreme heat events' },
  { id: 'cold', name: 'Cold Wave', field: 'cold', icon: <ColdIcon />, color: '#00BCD4', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Extreme cold events' },
  { id: 'tornado', name: 'Tornado', field: 'tornado', icon: <WindIcon />, color: '#E91E63', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Tornado risk' },
  { id: 'wind', name: 'Strong Wind', field: 'wind', icon: <WindIcon />, color: '#9E9E9E', category: 'hazard', scale: { min: 0, max: 10 }, description: 'High wind events' },
  { id: 'hail', name: 'Hail', field: 'hail', icon: <ColdIcon />, color: '#607D8B', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Hailstorm risk' },
  { id: 'ice', name: 'Ice Storm', field: 'ice', icon: <ColdIcon />, color: '#00E5FF', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Ice accumulation risk' },
  { id: 'winter', name: 'Winter Storm', field: 'winter', icon: <ColdIcon />, color: '#B3E5FC', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Heavy snow risk' },
  { id: 'lightning', name: 'Lightning', field: 'lightning', icon: <LightningIcon />, color: '#FFEB3B', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Lightning strike risk' },
  { id: 'avalanche', name: 'Avalanche', field: 'avalanche', icon: <EarthquakeIcon />, color: '#ECEFF1', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Avalanche risk' },
  { id: 'landslide', name: 'Landslide', field: 'landslide', icon: <EarthquakeIcon />, color: '#A1887F', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Landslide risk' },
  { id: 'tsunami', name: 'Tsunami', field: 'tsunami', icon: <WaterDropIcon />, color: '#006064', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Tsunami risk' },
  { id: 'volcanic', name: 'Volcanic', field: 'volcanic', icon: <WarningIcon />, color: '#BF360C', category: 'hazard', scale: { min: 0, max: 10 }, description: 'Volcanic activity risk' },
  
  // 3 Environmental
  { id: 'env_risk', name: 'Environmental Risk', field: 'environmental_risk_score', icon: <WarningIcon />, color: '#4CAF50', category: 'environmental', scale: { min: 0, max: 10 }, description: 'Overall environmental risk' },
  { id: 'superfund', name: 'Superfund Sites', field: 'superfund_sites', icon: <WarningIcon />, color: '#8BC34A', category: 'environmental', scale: { min: 0, max: 100 }, description: 'Number of superfund sites' },
  { id: 'epa_facilities', name: 'EPA Facilities', field: 'epa_facilities', icon: <WarningIcon />, color: '#CDDC39', category: 'environmental', scale: { min: 0, max: 100 }, description: 'EPA regulated facilities' },
  
  // 12 Demographics
  { id: 'toxic_air', name: 'Toxic Air %tile', field: 'toxic_air_percentile', icon: <WindIcon />, color: '#F44336', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Air toxics percentile' },
  { id: 'drinking_water', name: 'Drinking Water %tile', field: 'drinking_water_percentile', icon: <WaterDropIcon />, color: '#2196F3', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Drinking water quality' },
  { id: 'wastewater', name: 'Wastewater %tile', field: 'wastewater_percentile', icon: <WaterDropIcon />, color: '#03A9F4', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Wastewater discharge' },
  { id: 'pm25', name: 'PM2.5 %tile', field: 'pm25_percentile', icon: <WindIcon />, color: '#FF9800', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Particulate matter' },
  { id: 'ozone', name: 'Ozone %tile', field: 'ozone_percentile', icon: <WindIcon />, color: '#FFC107', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Ozone concentration' },
  { id: 'traffic', name: 'Traffic %tile', field: 'traffic_percentile', icon: <WindIcon />, color: '#9E9E9E', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Traffic proximity' },
  { id: 'lead_paint', name: 'Lead Paint %tile', field: 'lead_paint_percentile', icon: <WarningIcon />, color: '#795548', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Lead paint exposure' },
  { id: 'superfund_pct', name: 'Superfund %tile', field: 'superfund_percentile', icon: <WarningIcon />, color: '#E91E63', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Superfund proximity' },
  { id: 'rmp', name: 'RMP %tile', field: 'rmp_percentile', icon: <WarningIcon />, color: '#9C27B0', category: 'demographic', scale: { min: 0, max: 100 }, description: 'RMP facility percentile' },
  { id: 'tsdf', name: 'TSDF %tile', field: 'tsdf_percentile', icon: <WarningIcon />, color: '#673AB7', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Hazardous waste facility' },
  { id: 'underground_tanks', name: 'Underground Tanks %tile', field: 'underground_tanks_percentile', icon: <WarningIcon />, color: '#3F51B5', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Underground storage tanks' },
  { id: 'low_income', name: 'Low Income %tile', field: 'low_income_percentile', icon: <LocationCityIcon />, color: '#607D8B', category: 'demographic', scale: { min: 0, max: 100 }, description: 'Low income population' },
  
  // 4 Financial
  { id: 'population', name: 'Population', field: 'population', icon: <LocationCityIcon />, color: '#00BCD4', category: 'financial', scale: { min: 0, max: 1000000 }, description: 'Total population' },
  { id: 'building_value', name: 'Building Value', field: 'building_value', icon: <LocationCityIcon />, color: '#009688', category: 'financial', scale: { min: 0, max: 1000000000 }, description: 'Total building value' },
  { id: 'resilience', name: 'Resilience Score', field: 'resilience', icon: <CheckCircleIcon />, color: '#4CAF50', category: 'financial', scale: { min: 0, max: 100 }, description: 'Community resilience' },
  { id: 'community_risk', name: 'Community Risk', field: 'community_risk', icon: <WarningIcon />, color: '#FF5722', category: 'financial', scale: { min: 0, max: 10 }, description: 'Community risk index' },
];

// =============== CONVEX HULL HELPER ===============

function convexHull(points: [number, number][]): [number, number][] {
  if (points.length < 3) return points;
  
  points.sort((a, b) => a[0] !== b[0] ? a[0] - b[0] : a[1] - b[1]);
  
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  
  const lower: [number, number][] = [];
  for (const p of points) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  
  const upper: [number, number][] = [];
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

// =============== MAIN COMPONENT ===============

const CombinedRiskMap: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const mapLoadedRef = useRef(false);
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const hasUserMovedMap = useRef(false);
  
  // City boundaries cache - stores GeoJSON polygons for each city
  const cityBoundariesCache = useRef<Map<string, GeoJSON.Feature | null>>(new Map());
  
  // Mode state
  const [mode, setMode] = useState<'discovery' | 'portfolio'>('discovery');
  
  // Data state
  const [loading, setLoading] = useState(false);
  const [allLocations, setAllLocations] = useState<PortfolioItem[]>([]);
  const [portfolioName, setPortfolioName] = useState('');
  
  // Discovery mode state (Dec 8 features)
  const [viewMode, setViewMode] = useState<'cities' | 'postal'>('cities');
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [heatmapIntensity, setHeatmapIntensity] = useState(0.6);
  const [cityData, setCityData] = useState<CityAggregate[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityAggregate | null>(null);
  const selectedCityRef = useRef<CityAggregate | null>(null); // Ref for zoom handler
  const [loadedCities, setLoadedCities] = useState<string[]>([]);
  const [boundariesLoaded, setBoundariesLoaded] = useState(0); // Counter to trigger re-render when boundaries load
  
  // Portfolio mode state (Dec 9 features)
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [currentScenario, setCurrentScenario] = useState<string>('baseline');
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  
  // Shared state
  const [selectedLayers, setSelectedLayers] = useState<LayerSelection[]>([]);
  const [overlapMode, setOverlapMode] = useState<'AND' | 'OR'>('AND');
  const [selectedLocation, setSelectedLocation] = useState<PortfolioItem | null>(null);
  
  // UI state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [layerDialogOpen, setLayerDialogOpen] = useState(false);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [buildMode, setBuildMode] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [areaSelectMode, setAreaSelectMode] = useState(false);
  const [selectedAreaBounds, setSelectedAreaBounds] = useState<mapboxgl.LngLatBounds | null>(null);
  
  // Click-to-explore state
  const [locationDetailOpen, setLocationDetailOpen] = useState(false);
  const [clickedLocation, setClickedLocation] = useState<PortfolioItem | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [addToPortfolioValue, setAddToPortfolioValue] = useState<string>('');
  
  // Fullscreen state
  const [fullscreenMap, setFullscreenMap] = useState(false);
  
  // =============== HELPER FUNCTIONS ===============

  const getFieldValue = (item: PortfolioItem, field: string): number => {
    const val = item[field];
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val) || 0;
    return 0;
  };

  const getLayerById = (id: string) => ALL_LAYERS.find(l => l.id === id);

  // =============== DATA LOADING ===============

  // Check for portfolio data on mount - auto-switch to portfolio mode if data exists
  useEffect(() => {
    const checkForPortfolioData = async () => {
      try {
        const mapData = await getMapData();
        if (mapData) {
          console.log('📦 Found mappedPortfolio in IndexedDB, switching to portfolio mode');
          setMode('portfolio');
        }
      } catch (err) {
        console.error('Error checking for portfolio data:', err);
      }
    };
    checkForPortfolioData();
  }, []);

  useEffect(() => {
    if (mode === 'portfolio') {
      loadPortfolioData();
    }
  }, [mode]);

// Handle navigation state from Dashboard (City Explorer)
useEffect(() => {
  const navState = location.state as MapNavigationState | null;
  
  if (navState) {
    console.log('Map received navigation state:', navState);
    
    // Set mode if specified
    if (navState.mode === 'discovery') {
      // Clear portfolio data to ensure clean discovery mode
      clearMapData().catch(err => console.error('Error clearing map data:', err));
      setMode('discovery');
      setAllLocations([]);
      setPortfolioName('');
      setSelectedCity(null);
      setLoadedCities([]);
      
      // Load random city if requested
      if (navState.loadRandomCity) {
        console.log('Loading random city...');
        // Small delay to ensure map is initialized
        setTimeout(() => {
          loadRandomCity();
        }, 800);
      }
    }
// Clear the navigation state so it doesn't persist on refresh
    window.history.replaceState({}, document.title);
  }
}, [location.state]);

  const loadPortfolioData = async () => {
    setLoading(true);
    try {
      // Try IndexedDB first, fall back to localStorage for backwards compatibility
      let data = await getMapData();
      
      // Fallback to localStorage if IndexedDB is empty (migration support)
      if (!data) {
        const localData = localStorage.getItem('lastMappedPortfolio') || localStorage.getItem('mappedPortfolio');
        if (localData) {
          data = JSON.parse(localData);
          console.log('Loaded portfolio from localStorage (legacy)');
        }
      }
      
      if (!data) {
        console.log('No portfolio found in cache');
        setLoading(false);
        return;
      }

      const items = data.locations || data.items || [];
      
      const normalizedItems = items.map((item: any) => {
        // Parse coordinates properly
        let lat = 0;
        let lng = 0;
        
        if (item.latitude !== undefined && item.latitude !== null) {
          lat = typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude);
        } else if (item.lat !== undefined && item.lat !== null) {
          lat = typeof item.lat === 'number' ? item.lat : parseFloat(item.lat);
        }
        
        if (item.longitude !== undefined && item.longitude !== null) {
          lng = typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude);
        } else if (item.lng !== undefined && item.lng !== null) {
          lng = typeof item.lng === 'number' ? item.lng : parseFloat(item.lng);
        }
        
        // Use 0 if NaN
        if (isNaN(lat)) lat = 0;
        if (isNaN(lng)) lng = 0;
        
        return {
          postal_code: item.postal_code || item.postalCode,
          city: item.city || 'Unknown',
          state: item.state || '',
          latitude: lat,
          longitude: lng,
          risk_score: item.risk_score || item.riskScore || 0,
          asset_value: item.asset_value || item.assetValue || 0,
          hurricane: item.hurricane || 0,
          flood: item.flood || 0,
          flood_r: item.flood_r || 0,
          wildfire: item.wildfire || 0,
          earthquake: item.earthquake || 0,
          drought: item.drought || 0,
          heat: item.heat || 0,
          cold: item.cold || 0,
          tornado: item.tornado || 0,
          wind: item.wind || 0,
          hail: item.hail || 0,
          ice: item.ice || 0,
          winter: item.winter || 0,
          lightning: item.lightning || 0,
          landslide: item.landslide || 0,
          tsunami: item.tsunami || 0,
          volcanic: item.volcanic || 0,
          avalanche: item.avalanche || 0,
          ...item,
        };
      });
      
      setAllLocations(normalizedItems);
      setPortfolioName(data.portfolioName || 'Portfolio');
      
      // Log coordinate validation
      const invalidCoords = normalizedItems.filter((i: PortfolioItem) => 
        isNaN(i.latitude as number) || isNaN(i.longitude as number) || i.latitude === 0 || i.longitude === 0
      );
      if (invalidCoords.length > 0) {
        console.warn(`Found ${invalidCoords.length} items with invalid coordinates:`, invalidCoords.slice(0, 3));
      }
      
      const baseline: Scenario = {
        id: 'baseline',
        name: 'Baseline',
        items: normalizedItems,
        totalValue: normalizedItems.reduce((sum: number, i: PortfolioItem) => sum + getFieldValue(i, 'asset_value'), 0),
        avgRisk: normalizedItems.reduce((sum: number, i: PortfolioItem) => sum + getFieldValue(i, 'risk_score'), 0) / (normalizedItems.length || 1),
        propertyCount: normalizedItems.length,
        modifications: [],
      };
      
      setScenarios([baseline]);
      setLoading(false);
      
      // Fit map to portfolio bounds
      if (normalizedItems.length > 0 && mapRef.current) {
        const validItems = normalizedItems.filter((i: PortfolioItem) => 
          i.latitude && i.longitude && i.latitude !== 0 && i.longitude !== 0
        );
        
        if (validItems.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          validItems.forEach((item: PortfolioItem) => {
            bounds.extend([item.longitude as number, item.latitude as number]);
          });
          
          // Add some padding
          mapRef.current.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 10,
            duration: 1000
          });
        }
      }
      
      // Auto-add top risk layers based on portfolio data
      if (normalizedItems.length > 0 && selectedLayers.length === 0) {
        // Calculate which hazards have the highest average values
        const hazardFields = ['hurricane', 'flood', 'wildfire', 'earthquake', 'heat', 'tornado', 'wind', 'drought'];
        const hazardAvgs = hazardFields.map(field => ({
          field,
          avg: normalizedItems.reduce((sum: number, i: PortfolioItem) => sum + (i[field] || 0), 0) / normalizedItems.length
        }));
        
        // Sort by average and pick top 2 hazards with values > 0
        const topHazards = hazardAvgs
          .filter(h => h.avg > 0)
          .sort((a, b) => b.avg - a.avg)
          .slice(0, 2);
        
        if (topHazards.length > 0) {
          const defaultLayers: LayerSelection[] = topHazards.map((hazard, index) => {
            const layer = ALL_LAYERS.find(l => l.field === hazard.field);
            return {
              layerId: layer?.id || hazard.field,
              visible: true,
              opacity: 0.7,
              threshold: 0,
              isBase: index === 0
            };
          });
          
          setSelectedLayers(defaultLayers);
          console.log(`Auto-added risk layers based on portfolio: ${topHazards.map(h => h.field).join(', ')}`);
        }
      }
    } catch (err) {
      console.error('Error loading portfolio:', err);
      setLoading(false);
    }
  };

  // =============== CITY AGGREGATION (Discovery Mode) ===============

  const cityAggregates = useMemo(() => {
    if (mode !== 'discovery' || !allLocations.length) return [];
    
    const cityMap = new Map<string, PortfolioItem[]>();
    
    allLocations.forEach(item => {
      const key = `${item.city}, ${item.state}`;
      if (!cityMap.has(key)) cityMap.set(key, []);
      cityMap.get(key)!.push(item);
    });
    
    const aggregates: CityAggregate[] = [];
    cityMap.forEach((items, key) => {
      const [city, state] = key.split(', ');
      
      const avgRisk = items.reduce((sum, i) => sum + (i.risk_score || 0), 0) / items.length;
      
      const layerValues: { [field: string]: number } = {};
      selectedLayers.forEach(layer => {
        const layerDef = getLayerById(layer.layerId);
        if (!layerDef) return;
        const sum = items.reduce((s, i) => s + getFieldValue(i, layerDef.field), 0);
        layerValues[layerDef.field] = sum / items.length;
      });
      
      aggregates.push({ city, state, postalCodes: items, avgRisk, count: items.length, layerValues });
    });
    
    return aggregates;
  }, [allLocations, selectedLayers, mode]);

  // =============== FILTERING ===============

  const filteredData = useMemo(() => {
    let items = mode === 'discovery' ? allLocations : 
                (scenarios.find(s => s.id === currentScenario)?.items || allLocations);
    
    console.log(`Starting with ${items.length} items in ${mode} mode`);
    
    if (!selectedLayers.length || selectedLayers.every(l => !l.visible)) {
      return items;
    }
    
    const visibleLayers = selectedLayers.filter(l => l.visible);
    console.log(`Filtering with ${visibleLayers.length} visible layers:`, 
      visibleLayers.map(l => {
        const def = getLayerById(l.layerId);
        return `${def?.name} >= ${l.threshold}`;
      })
    );
    
    items = items.filter(item => {
      if (overlapMode === 'AND') {
        return visibleLayers.every(layer => {
          const layerDef = getLayerById(layer.layerId);
          if (!layerDef) return true;
          const value = getFieldValue(item, layerDef.field);
          return value >= layer.threshold;
        });
      } else {
        return visibleLayers.some(layer => {
          const layerDef = getLayerById(layer.layerId);
          if (!layerDef) return false;
          const value = getFieldValue(item, layerDef.field);
          return value >= layer.threshold;
        });
      }
    });
    
    console.log(`After layer filtering: ${items.length} items remaining`);
    
    // Apply portfolio mode filters
    if (mode === 'portfolio') {
      filters.forEach(filter => {
        if (!filter.enabled) return;
        items = items.filter(item => {
          const value = getFieldValue(item, filter.field);
          switch (filter.operator) {
            case '>=': return value >= filter.value;
            case '<=': return value <= filter.value;
            case '>': return value > filter.value;
            case '<': return value < filter.value;
            case '=': return Math.abs(value - filter.value) < 0.01;
            case 'between': return value >= filter.value && value <= (filter.value2 || filter.value);
            default: return true;
          }
        });
      });
    }
    
    return items;
  }, [allLocations, selectedLayers, overlapMode, mode, currentScenario, scenarios, filters]);

  const filteredCities = useMemo(() => {
    if (mode !== 'discovery' || viewMode !== 'cities') return [];
    
    if (!selectedLayers.length || selectedLayers.every(l => !l.visible)) {
      return cityAggregates;
    }
    
    const visibleLayers = selectedLayers.filter(l => l.visible);
    
    return cityAggregates.filter(city => {
      if (overlapMode === 'AND') {
        return visibleLayers.every(layer => {
          const layerDef = getLayerById(layer.layerId);
          if (!layerDef) return true;
          const value = city.layerValues[layerDef.field] || 0;
          return value >= layer.threshold;
        });
      } else {
        return visibleLayers.some(layer => {
          const layerDef = getLayerById(layer.layerId);
          if (!layerDef) return false;
          const value = city.layerValues[layerDef.field] || 0;
          return value >= layer.threshold;
        });
      }
    });
  }, [cityAggregates, selectedLayers, overlapMode, mode, viewMode]);

  // =============== AUTO-SEARCH (Discovery Mode) ===============

  useEffect(() => {
    if (mode !== 'discovery') return;
    if (!selectedLayers.length || selectedLayers.every(l => !l.visible)) return;
    
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    searchDebounceTimer.current = setTimeout(() => {
      performAutoSearch();
    }, 500);
    
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [selectedLayers, overlapMode, mode]);

  const performAutoSearch = async () => {
    const visibleLayers = selectedLayers.filter(l => l.visible);
    if (!visibleLayers.length) return;
    
    setLoading(true);
    try {
      // Build layers array for POST /api/search/by-hazards endpoint
      const layers = visibleLayers.map(layer => {
        const layerDef = getLayerById(layer.layerId);
        return {
          field: layerDef?.field || '',
          threshold: layer.threshold
        };
      }).filter(l => l.field);
      
      console.log('Auto-search: Calling /by-hazards with', { layers, overlapMode });
      
      const response = await axios.post(`${API_URL}/search/by-hazards`, {
        layers,
        overlapMode: overlapMode === 'AND',
        limit: 500
      });
      
      const locations = response.data.results || [];
      console.log(`Auto-search: Found ${locations.length} locations`);
      
      setAllLocations(locations);
      
      if (!hasUserMovedMap.current && mapRef.current && locations.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        locations.forEach((loc: PortfolioItem) => {
          const lat = typeof loc.latitude === 'number' ? loc.latitude : parseFloat(loc.latitude as any);
          const lng = typeof loc.longitude === 'number' ? loc.longitude : parseFloat(loc.longitude as any);
          
          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            bounds.extend([lng, lat]);
          }
        });
        if (!bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, { padding: 100, duration: 1000 });
        }
      }
    } catch (error) {
      console.error('Auto-search error:', error);
    } finally {
      setLoading(false);
    }
  };

// List of major US cities for random exploration
const RANDOM_CITIES = [
  { city: 'Austin', state: 'TX' },
  { city: 'Denver', state: 'CO' },
  { city: 'Seattle', state: 'WA' },
  { city: 'Phoenix', state: 'AZ' },
  { city: 'Atlanta', state: 'GA' },
  { city: 'Boston', state: 'MA' },
  { city: 'Nashville', state: 'TN' },
  { city: 'Portland', state: 'OR' },
  { city: 'San Diego', state: 'CA' },
  { city: 'Minneapolis', state: 'MN' },
  { city: 'Miami', state: 'FL' },
  { city: 'Charlotte', state: 'NC' },
  { city: 'Detroit', state: 'MI' },
  { city: 'Tampa', state: 'FL' },
  { city: 'Orlando', state: 'FL' },
  { city: 'Salt Lake City', state: 'UT' },
  { city: 'Kansas City', state: 'MO' },
  { city: 'Indianapolis', state: 'IN' },
  { city: 'Columbus', state: 'OH' },
  { city: 'San Antonio', state: 'TX' },
  { city: 'Pittsburgh', state: 'PA' },
  { city: 'Cincinnati', state: 'OH' },
  { city: 'Cleveland', state: 'OH' },
  { city: 'Sacramento', state: 'CA' },
  { city: 'Las Vegas', state: 'NV' },
  { city: 'Milwaukee', state: 'WI' },
  { city: 'Raleigh', state: 'NC' },
  { city: 'Richmond', state: 'VA' },
  { city: 'New Orleans', state: 'LA' },
  { city: 'Memphis', state: 'TN' },
];

const loadRandomCity = async () => {
  setLoading(true);
  try {
    // Pick a random city
    const randomCity = RANDOM_CITIES[Math.floor(Math.random() * RANDOM_CITIES.length)];
    console.log(`Loading random city: ${randomCity.city}, ${randomCity.state}`);
    
    // Search for the city using the search endpoint
    const response = await axios.get(`${API_URL}/search/locations`, {
      params: { query: `${randomCity.city} ${randomCity.state}` }
    });
    
    const results = response.data.results || [];
    
    if (results.length > 0) {
      // Find city result or use first result
      const cityResult = results.find((r: any) => r.search_type === 'city') || results[0];
      
      if (cityResult) {
        // Load the city data
        const locations = cityResult.locations || [cityResult];
        setAllLocations(locations);
        setLoadedCities([`${randomCity.city}, ${randomCity.state}`]);
        
        // Set view mode to postal to show individual locations
        setViewMode('postal');
        
        // Fly to the city
        if (mapRef.current) {
          if (cityResult.latitude && cityResult.longitude) {
            mapRef.current.flyTo({
              center: [cityResult.longitude, cityResult.latitude],
              zoom: 11,
              duration: 1500
            });
          } else if (locations.length > 0) {
            // Calculate bounds from locations
            const bounds = new mapboxgl.LngLatBounds();
            locations.forEach((loc: any) => {
              if (loc.latitude && loc.longitude) {
                bounds.extend([loc.longitude, loc.latitude]);
              }
            });
            if (!bounds.isEmpty()) {
              mapRef.current.fitBounds(bounds, { padding: 50, duration: 1500 });
            }
          }
        }
        
        console.log(`Loaded ${locations.length} locations for ${randomCity.city}, ${randomCity.state}`);
      }
    } else {
      console.log(`No results found for ${randomCity.city}, trying another...`);
      // Try another random city (recursive, but limited by API responses)
      loadRandomCity();
    }
  } catch (error) {
    console.error('Error loading random city:', error);
    setLoading(false);
  } finally {
    setLoading(false);
  }
};

  // Keep selectedCity ref in sync for zoom handler
  useEffect(() => {
    selectedCityRef.current = selectedCity;
  }, [selectedCity]);

  // Ref to hold the click handler function so it can access latest state
  const handleMapClickRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);
  
  // Update the click handler ref whenever dependencies change
  useEffect(() => {
    handleMapClickRef.current = async (e: mapboxgl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      console.log(`Map clicked at: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      
      setLoadingLocation(true);
      setLocationDetailOpen(true);
      setClickedLocation(null);
      setAddToPortfolioValue('');
      
      try {
        console.log('Searching for locations near click point...');
        
        // Try the dedicated coordinates endpoint first
        let response;
        try {
          response = await axios.get(`${API_URL}/search/by-coordinates`, {
            params: { lat, lng, radius: 100 }
          });
          console.log('Coordinates search response:', response.data);
        } catch (coordError) {
          // Fallback to by-hazards with larger bounds
          console.log('Coordinates endpoint not available, using fallback...');
          response = await axios.post(`${API_URL}/search/by-hazards`, {
            layers: [{ field: 'hurricane', threshold: 0 }],
            overlapMode: true,
            limit: 500,
            bounds: {
              minLat: lat - 1.0,
              maxLat: lat + 1.0,
              minLng: lng - 1.0,
              maxLng: lng + 1.0
            }
          });
          console.log('Fallback search response:', response.data);
        }
        
        const results = response.data.results || [];
        
        if (results.length > 0) {
          // Find closest location to clicked point
          let closest = results[0];
          let minDist = Infinity;
          
          results.forEach((loc: PortfolioItem) => {
            const locLat = loc.latitude || 0;
            const locLng = loc.longitude || 0;
            const dist = Math.sqrt(Math.pow(locLat - lat, 2) + Math.pow(locLng - lng, 2));
            if (dist < minDist) {
              minDist = dist;
              closest = loc;
            }
          });
          
          setClickedLocation(closest);
          console.log('Found location:', closest.postal_code, closest.city, closest.state);
        } else {
          console.log('No location found near clicked coordinates');
          setClickedLocation(null);
        }
      } catch (error: any) {
        console.error('Error fetching location data:', error.message || error);
        setClickedLocation(null);
      } finally {
        setLoadingLocation(false);
      }
    };
  }, []);

  // =============== MAP INITIALIZATION ===============

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token') return;
    
    mapboxgl.accessToken = MAPBOX_TOKEN;
    
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-98.5795, 39.8283], // Center of US
      zoom: 4,
      projection: 'mercator', // Flat map projection
      maxBounds: [[-180, 5], [-50, 72]], // Restrict to roughly North America
    });
    
    map.on('load', () => {
      mapLoadedRef.current = true;
      
      map.addSource('city-boundary', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      
      map.addLayer({
        id: 'city-boundary-line',
        type: 'line',
        source: 'city-boundary',
        paint: {
          'line-color': '#2196F3',
          'line-width': 3,
          'line-dasharray': [2, 2],
        },
      });
      
      map.addLayer({
        id: 'city-boundary-fill',
        type: 'fill',
        source: 'city-boundary',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.1,
        },
      });
    });
    
    map.on('move', () => {
      hasUserMovedMap.current = true;
    });
    
    // Click handler for exploring locations - click anywhere on map to get risk data
    map.on('click', (e) => {
      // Check if we clicked on a marker element (don't trigger if clicking marker)
      const target = e.originalEvent.target as HTMLElement;
      if (target && (target.closest('.city-ring-marker') || target.closest('.stacked-ring-marker') || target.closest('.mapboxgl-marker'))) {
        console.log('Clicked on marker, ignoring map click');
        return;
      }
      
      // Call the ref-based handler
      if (handleMapClickRef.current) {
        handleMapClickRef.current(e);
      }
    });
    
    // Clear selected city when user zooms out significantly
    map.on('zoomend', () => {
      const currentZoom = map.getZoom();
      if (currentZoom < 6 && selectedCityRef.current) {
        // User zoomed out - clear selection to show all cities again
        setSelectedCity(null);
        // Clear city boundary source
        const source = map.getSource('city-boundary') as mapboxgl.GeoJSONSource;
        if (source) {
          source.setData({ type: 'FeatureCollection', features: [] });
        }
      }
    });
    
    mapRef.current = map;
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // =============== FETCH REAL CITY BOUNDARIES FROM TIGER API ===============
  
  // Fetch actual city boundary from your TIGER database API
  const fetchCityBoundary = useCallback(async (city: string, state: string): Promise<GeoJSON.Feature | null> => {
    // Trim whitespace from city and state names
    const trimmedCity = city.trim();
    const trimmedState = state.trim();
    const cacheKey = `${trimmedCity}, ${trimmedState}`;
    
    // Check cache first
    if (cityBoundariesCache.current.has(cacheKey)) {
      return cityBoundariesCache.current.get(cacheKey) || null;
    }
    
    try {
      const response = await axios.get(`${API_URL}/city-boundaries/boundary`, {
        params: { city: trimmedCity, state: trimmedState }
      });
      
      const feature = response.data as GeoJSON.Feature;
      cityBoundariesCache.current.set(cacheKey, feature);
      console.log(`Loaded boundary for ${cacheKey}`);
      return feature;
      
    } catch (error: any) {
      // 404 means city not found - that's ok, cache it as null
      if (error.response?.status === 404) {
        console.log(`No boundary found for ${cacheKey}`);
      } else {
        console.warn(`Error fetching boundary for ${cacheKey}:`, error.message);
      }
      cityBoundariesCache.current.set(cacheKey, null);
      return null;
    }
  }, []);
  
  // Batch fetch city boundaries for multiple cities (uses POST endpoint)
  const fetchCityBoundaries = useCallback(async (cities: Array<{city: string, state: string}>): Promise<Map<string, GeoJSON.Feature | null>> => {
    const results = new Map<string, GeoJSON.Feature | null>();
    
    // Trim and dedupe cities
    const trimmedCities = cities.map(c => ({
      city: c.city.trim(),
      state: c.state.trim()
    }));
    
    // Filter out already cached cities
    const uncachedCities = trimmedCities.filter(c => !cityBoundariesCache.current.has(`${c.city}, ${c.state}`));
    
    if (uncachedCities.length === 0) {
      // All cities already cached
      trimmedCities.forEach(c => {
        const key = `${c.city}, ${c.state}`;
        results.set(key, cityBoundariesCache.current.get(key) || null);
      });
      return results;
    }
    
    try {
      console.log(`Fetching ${uncachedCities.length} city boundaries from TIGER API...`);
      
      const response = await axios.post(`${API_URL}/city-boundaries/batch`, {
        cities: uncachedCities
      });
      
      const featureCollection = response.data;
      
      // Cache the results
      if (featureCollection.features) {
        featureCollection.features.forEach((feature: GeoJSON.Feature) => {
          const name = feature.properties?.name;
          const state = feature.properties?.state;
          if (name && state) {
            const key = `${name}, ${state}`;
            cityBoundariesCache.current.set(key, feature);
            results.set(key, feature);
          }
        });
      }
      
      // Mark cities not found as null in cache
      uncachedCities.forEach(c => {
        const key = `${c.city}, ${c.state}`;
        if (!results.has(key)) {
          cityBoundariesCache.current.set(key, null);
          results.set(key, null);
        }
      });
      
      console.log(`Loaded ${featureCollection.features?.length || 0} boundaries from TIGER API`);
      
    } catch (error: any) {
      console.warn('Error batch fetching boundaries:', error.message);
      // Mark all as null on error
      uncachedCities.forEach(c => {
        const key = `${c.city}, ${c.state}`;
        cityBoundariesCache.current.set(key, null);
        results.set(key, null);
      });
    }
    
    return results;
  }, []);

  // =============== CITY BOUNDARY DRAWING ===============

  const drawCityBoundary = useCallback(async (city: CityAggregate) => {
    if (!mapRef.current || !mapLoadedRef.current) return;
    
    // Get visible layer to determine color
    const visibleLayers = selectedLayers.filter(l => l.visible);
    const primaryLayer = visibleLayers.length > 0 ? getLayerById(visibleLayers[0].layerId) : null;
    
    // Calculate average hazard value for the city
    let avgHazardValue = 0;
    if (primaryLayer && city.postalCodes.length > 0) {
      let total = 0;
      city.postalCodes.forEach(pc => {
        total += getFieldValue(pc, primaryLayer.field);
      });
      avgHazardValue = total / city.postalCodes.length;
    }
    
    // Try to get real TIGER boundary first
    const cacheKey = `${city.city}, ${city.state}`;
    let boundaryFeature = cityBoundariesCache.current.get(cacheKey);
    
    // If not cached, try to fetch it
    if (!boundaryFeature && city.city && city.state) {
      boundaryFeature = await fetchCityBoundary(city.city, city.state);
    }
    
    let geojson: GeoJSON.FeatureCollection;
    
    if (boundaryFeature && boundaryFeature.geometry) {
      // Use REAL TIGER boundary
      console.log(`Using TIGER boundary for ${cacheKey}`);
      geojson = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { 
            city: city.city, 
            state: city.state,
            avgHazardValue: avgHazardValue
          },
          geometry: boundaryFeature.geometry
        }]
      };
    } else {
      // Fallback to convex hull
      console.log(`Using convex hull fallback for ${cacheKey}`);
      const points: [number, number][] = city.postalCodes
        .filter(p => {
          const lat = typeof p.latitude === 'number' ? p.latitude : parseFloat(p.latitude as any);
          const lng = typeof p.longitude === 'number' ? p.longitude : parseFloat(p.longitude as any);
          return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        })
        .map(p => {
          const lat = typeof p.latitude === 'number' ? p.latitude : parseFloat(p.latitude as any);
          const lng = typeof p.longitude === 'number' ? p.longitude : parseFloat(p.longitude as any);
          return [lng, lat] as [number, number];
        });
      
      if (points.length < 1) {
        const source = mapRef.current.getSource('city-boundary') as mapboxgl.GeoJSONSource;
        if (source) source.setData({ type: 'FeatureCollection', features: [] });
        return;
      }
      
      let hullCoords: [number, number][];
      
      if (points.length === 1) {
        // Single point - create a small hexagon
        const [cx, cy] = points[0];
        const radius = 0.015;
        hullCoords = [];
        for (let i = 0; i <= 6; i++) {
          const angle = (i / 6) * 2 * Math.PI;
          hullCoords.push([cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)]);
        }
      } else if (points.length === 2) {
        // Two points - create a rectangle
        const [p1, p2] = points;
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const len = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const nx = -dy / len * 0.01;
        const ny = dx / len * 0.01;
        hullCoords = [
          [p1[0] + nx, p1[1] + ny],
          [p2[0] + nx, p2[1] + ny],
          [p2[0] - nx, p2[1] - ny],
          [p1[0] - nx, p1[1] - ny],
          [p1[0] + nx, p1[1] + ny]
        ];
      } else {
        // 3+ points - compute convex hull
        const hull = convexHull(points);
        
        const centerLng = hull.reduce((s, p) => s + p[0], 0) / hull.length;
        const centerLat = hull.reduce((s, p) => s + p[1], 0) / hull.length;
        const paddedHull = hull.map(([lng, lat]) => {
          const scale = 1.15;
          return [
            centerLng + (lng - centerLng) * scale,
            centerLat + (lat - centerLat) * scale
          ] as [number, number];
        });
        hullCoords = [...paddedHull, paddedHull[0]];
      }
      
      geojson = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { 
            city: city.city, 
            state: city.state,
            avgHazardValue: avgHazardValue
          },
          geometry: {
            type: 'Polygon',
            coordinates: [hullCoords]
          }
        }]
      };
    }
    
    // Update the boundary source
    const source = mapRef.current.getSource('city-boundary') as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(geojson);
    }
    
    // Update boundary fill color based on hazard value
    if (mapRef.current.getLayer('city-boundary-fill') && primaryLayer) {
      const intensity = Math.min(1, avgHazardValue / 10);
      const hexColor = primaryLayer.color;
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      
      // Lighter color for lower values, full color for higher
      const fillColor = `rgba(${r}, ${g}, ${b}, ${0.15 + intensity * 0.35})`;
      const strokeColor = primaryLayer.color;
      
      mapRef.current.setPaintProperty('city-boundary-fill', 'fill-color', fillColor);
      mapRef.current.setPaintProperty('city-boundary-line', 'line-color', strokeColor);
    }
    
  }, [selectedLayers, fetchCityBoundary]);

  const clearCityBoundary = useCallback(() => {
    if (!mapRef.current || !mapLoadedRef.current) return;
    const source = mapRef.current.getSource('city-boundary') as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({ type: 'FeatureCollection', features: [] });
    }
  }, []);

  // =============== CITY SELECTION ===============

  const handleSelectCity = useCallback((city: CityAggregate) => {
    setSelectedCity(city);
    setViewMode('cities');
    drawCityBoundary(city);
    
    if (mapRef.current) {
      const bounds = new mapboxgl.LngLatBounds();
      city.postalCodes.forEach(p => {
        const lat = typeof p.latitude === 'number' ? p.latitude : parseFloat(p.latitude as any);
        const lng = typeof p.longitude === 'number' ? p.longitude : parseFloat(p.longitude as any);
        
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          bounds.extend([lng, lat]);
        }
      });
      if (!bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds, { padding: 100, maxZoom: 12, duration: 800 });
      }
    }
  }, [drawCityBoundary]);

  const handleBackToCities = useCallback(() => {
    setSelectedCity(null);
    clearCityBoundary();
    hasUserMovedMap.current = false;
  }, [clearCityBoundary]);

  // =============== MARKERS ===============

  const updateMarkers = useCallback(() => {
    if (!mapRef.current) return;
    
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // In Discovery mode, only show markers if we have search results
    // Don't show portfolio data in Discovery mode
    if (mode === 'discovery' && allLocations.length === 0) {
      console.log('Discovery mode: No search results loaded, skipping marker render');
      return;
    }

    const visibleLayers = selectedLayers.filter(l => l.visible);
    const primaryLayer = visibleLayers.length > 0 ? getLayerById(visibleLayers[0].layerId) : null;

    // Get all visible layers for the fill layer
    const visibleLayerDefs = visibleLayers.map(l => ({
      ...l,
      def: getLayerById(l.layerId)
    })).filter(l => l.def);

    // ========== CREATE CITY-SHAPED FILLED POLYGONS ==========
    // This runs for BOTH city view and postal view
    // Creates smooth, natural-looking city boundary polygons
    if (showHeatmap && mapRef.current && mapLoadedRef.current && mapRef.current.isStyleLoaded() && visibleLayerDefs.length > 0 && filteredData.length > 0) {
      // Remove existing hazard layers and sources (clean up to 20 potential layers)
      for (let i = 0; i < 20; i++) {
        const fillLayerId = `hazard-fill-${i}`;
        const outlineLayerId = `hazard-outline-${i}`;
        const heatLayerId = `hazard-heat-${i}`;
        const sourceId = `hazard-source-${i}`;
        try {
          // Remove ALL layers before removing sources (order matters!)
          if (mapRef.current!.getLayer(fillLayerId)) {
            mapRef.current!.removeLayer(fillLayerId);
          }
          if (mapRef.current!.getLayer(outlineLayerId)) {
            mapRef.current!.removeLayer(outlineLayerId);
          }
          if (mapRef.current!.getLayer(heatLayerId)) {
            mapRef.current!.removeLayer(heatLayerId);
          }
          // Now safe to remove the source
          if (mapRef.current!.getSource(sourceId)) {
            mapRef.current!.removeSource(sourceId);
          }
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
      
      // Helper: Create a proper convex hull polygon from postal code points
      // This creates a city-shaped boundary using just the postal code locations
      const createCityConvexHull = (points: [number, number][]): [number, number][] => {
        if (points.length === 0) return [];
        
        if (points.length === 1) {
          // Single point - create a small hexagon around it
          const [cx, cy] = points[0];
          const radius = 0.02; // Small radius for single point
          const coords: [number, number][] = [];
          for (let i = 0; i <= 6; i++) {
            const angle = (i / 6) * 2 * Math.PI;
            coords.push([
              cx + radius * Math.cos(angle),
              cy + radius * Math.sin(angle)
            ]);
          }
          return coords;
        }
        
        if (points.length === 2) {
          // Two points - create a rectangle/capsule shape
          const [p1, p2] = points;
          const dx = p2[0] - p1[0];
          const dy = p2[1] - p1[1];
          const len = Math.sqrt(dx * dx + dy * dy);
          const nx = -dy / len * 0.015; // perpendicular offset
          const ny = dx / len * 0.015;
          return [
            [p1[0] + nx, p1[1] + ny],
            [p2[0] + nx, p2[1] + ny],
            [p2[0] - nx, p2[1] - ny],
            [p1[0] - nx, p1[1] - ny],
            [p1[0] + nx, p1[1] + ny] // close
          ];
        }
        
        // For 3+ points, compute convex hull directly
        const sortedPoints = [...points].sort((a, b) => a[0] !== b[0] ? a[0] - b[0] : a[1] - b[1]);
        
        const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
          (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
        
        const lower: [number, number][] = [];
        for (const p of sortedPoints) {
          while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
          }
          lower.push(p);
        }
        
        const upper: [number, number][] = [];
        for (let i = sortedPoints.length - 1; i >= 0; i--) {
          const p = sortedPoints[i];
          while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
          }
          upper.push(p);
        }
        
        upper.pop();
        lower.pop();
        let hull = lower.concat(upper);
        
        // Expand the hull slightly outward from center
        if (hull.length >= 3) {
          const centerX = hull.reduce((s, p) => s + p[0], 0) / hull.length;
          const centerY = hull.reduce((s, p) => s + p[1], 0) / hull.length;
          const expandFactor = 1.1; // 10% expansion
          
          hull = hull.map(([x, y]) => [
            centerX + (x - centerX) * expandFactor,
            centerY + (y - centerY) * expandFactor
          ] as [number, number]);
        }
        
        // Close the polygon
        if (hull.length > 0) {
          hull.push(hull[0]);
        }
        
        return hull;
      };
      
      // Group postal codes by city (trim whitespace from names)
      const cityGroups = new Map<string, { points: [number, number][], values: number[], items: typeof filteredData, city: string, state: string }>();
      
      filteredData.forEach(item => {
        const lat = typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude as any);
        const lng = typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude as any);
        if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return;
        
        // Trim whitespace from city and state
        const trimmedCity = (item.city || '').trim();
        const trimmedState = (item.state || '').trim();
        const cityKey = `${trimmedCity}, ${trimmedState}`;
        
        if (!cityGroups.has(cityKey)) {
          cityGroups.set(cityKey, { 
            points: [], 
            values: [], 
            items: [],
            city: trimmedCity,
            state: trimmedState
          });
        }
        const group = cityGroups.get(cityKey)!;
        group.points.push([lng, lat]);
        group.items.push(item);
      });
      
      // Check which cities need their boundaries fetched
      const citiesToFetch: Array<{city: string, state: string}> = [];
      cityGroups.forEach((group, cityKey) => {
        // cityKey is already trimmed from the grouping above
        if (!cityBoundariesCache.current.has(cityKey) && group.city && group.state) {
          citiesToFetch.push({ city: group.city, state: group.state });
        }
      });
      
      // Trigger async fetch for cities we don't have
      if (citiesToFetch.length > 0 && citiesToFetch.length <= 100) {
        // Fetch boundaries from TIGER API
        fetchCityBoundaries(citiesToFetch).then((results) => {
          // Force a re-render after boundaries are fetched by updating state
          const loadedCount = Array.from(results.values()).filter(f => f !== null).length;
          if (loadedCount > 0) {
            console.log(`Loaded ${loadedCount} TIGER city boundaries, triggering re-render`);
            setBoundariesLoaded(prev => prev + loadedCount);
          }
        });
      }
      
      // Process each visible layer
      visibleLayerDefs.forEach((layer, idx) => {
        const sourceId = `hazard-source-${idx}`;
        const fillLayerId = `hazard-fill-${idx}`;
        const outlineLayerId = `hazard-outline-${idx}`;
        
        // Skip if source already exists
        if (mapRef.current!.getSource(sourceId)) return;
        
        // Create city polygon features
        const features: GeoJSON.Feature[] = [];
        
        cityGroups.forEach((group, cityKey) => {
          // Calculate average value for this city for this layer
          let totalValue = 0;
          group.items.forEach(item => {
            totalValue += getFieldValue(item, layer.def!.field);
          });
          const avgValue = totalValue / group.items.length;
          const normalizedValue = Math.min(Math.max(avgValue / 10, 0), 1);
          
          // Try to get real city boundary from cache
          const cachedBoundary = cityBoundariesCache.current.get(cityKey);
          
          if (cachedBoundary && cachedBoundary.geometry) {
            // USE REAL CITY BOUNDARY from TIGER database
            features.push({
              type: 'Feature',
              properties: {
                city: cityKey,
                value: avgValue,
                normalizedValue: normalizedValue,
                postalCodeCount: group.items.length,
              },
              geometry: cachedBoundary.geometry
            });
          } else {
            // FALLBACK: Create convex hull from postal code points
            const cityPolygon = createCityConvexHull([...group.points]);
            
            if (cityPolygon.length < 4) return;
            
            features.push({
              type: 'Feature',
              properties: {
                city: cityKey,
                value: avgValue,
                normalizedValue: normalizedValue,
                postalCodeCount: group.items.length,
              },
              geometry: {
                type: 'Polygon',
                coordinates: [cityPolygon]
              }
            });
          }
        });
        
        try {
          // Add source with city polygon data
          mapRef.current!.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: features
            }
          });
        
          // Parse the layer color to RGB
          const hexColor = layer.def!.color;
          const r = parseInt(hexColor.slice(1, 3), 16);
          const g = parseInt(hexColor.slice(3, 5), 16);
          const b = parseInt(hexColor.slice(5, 7), 16);
          
          // Use per-layer opacity (default to 0.7 if not set)
          const layerOpacity = layer.opacity ?? 0.7;
          
          // Create 5-step color scale from light to dark
          const veryLowColor = `rgb(${Math.min(255, r + 180)}, ${Math.min(255, g + 180)}, ${Math.min(255, b + 180)})`;
          const lowColor = `rgb(${Math.min(255, r + 120)}, ${Math.min(255, g + 120)}, ${Math.min(255, b + 120)})`;
          const mediumColor = `rgb(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)})`;
          const highColor = `rgb(${r}, ${g}, ${b})`;
          const veryHighColor = `rgb(${Math.max(0, r - 50)}, ${Math.max(0, g - 50)}, ${Math.max(0, b - 50)})`;
          
          // Add FILL layer for city polygons
          mapRef.current!.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: sourceId,
            paint: {
              // Color based on normalized value - light to dark
              'fill-color': [
                'interpolate', ['linear'], ['get', 'normalizedValue'],
                0, veryLowColor,
                0.25, lowColor,
                0.5, mediumColor,
                0.75, highColor,
                1.0, veryHighColor
              ],
              // Apply layer opacity
              'fill-opacity': layerOpacity
            }
          }, 'city-boundary-fill');
          
          // Add outline layer for city boundaries
          mapRef.current!.addLayer({
            id: outlineLayerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': highColor,
              'line-width': 1,
              'line-opacity': layerOpacity * 0.5
            }
          }, 'city-boundary-fill');
          
        } catch (e) {
          console.warn('Error adding hazard layer:', e);
        }
      });
    }

    // In Discovery mode with cities view (not selected city) - show city markers
    if (mode === 'discovery' && viewMode === 'cities' && !selectedCity) {
      console.log(`Rendering ${filteredCities.length} city markers`);
      
      // Get ALL available hazard layers for the stacked rings
      const allHazardLayers: DataLayer[] = ALL_LAYERS.slice(0, 8);
      
      // Define type for layer values
      interface LayerValue {
        id: string;
        name: string;
        field: string;
        color: string;
        value: number;
        isElevated: boolean;
      }
      
      // City markers with stacked rings
      filteredCities.forEach(city => {
        const avgCoords = city.postalCodes.reduce(
          (acc, p) => {
            const lat = typeof p.latitude === 'number' ? p.latitude : parseFloat(p.latitude as any);
            const lng = typeof p.longitude === 'number' ? p.longitude : parseFloat(p.longitude as any);
            
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              acc.lat += lat;
              acc.lng += lng;
              acc.count++;
            }
            return acc;
          },
          { lat: 0, lng: 0, count: 0 }
        );
        
        if (avgCoords.count === 0) return;
        
        const avgLat = avgCoords.lat / avgCoords.count;
        const avgLng = avgCoords.lng / avgCoords.count;
        
        // Validate final coordinates
        if (isNaN(avgLat) || isNaN(avgLng)) return;
        
        // Calculate values for ALL hazard layers using city's layerValues
        const layerValues: LayerValue[] = allHazardLayers.map((layer: DataLayer) => ({
          id: layer.id,
          name: layer.name,
          field: layer.field,
          color: layer.color,
          value: city.layerValues[layer.field] || 0,
          isElevated: (city.layerValues[layer.field] || 0) >= 5
        }));
        
        // Sort by value descending
        const sortedLayers = [...layerValues].sort((a, b) => b.value - a.value);
        
        const avgRisk = layerValues.reduce((sum: number, l: LayerValue) => sum + l.value, 0) / layerValues.length;
        const maxRisk = Math.max(...layerValues.map((l: LayerValue) => l.value));
        const elevatedCount = layerValues.filter((l: LayerValue) => l.isElevated).length;
        
        // Base size for city markers (slightly larger)
        const baseSize = 44;
        const ringWidth = 4;
        const activeRings = sortedLayers.filter(l => l.value > 0).length;
        const totalSize = baseSize + (Math.min(activeRings, 5) * ringWidth * 2);
        
        // Create the marker container
        const el = document.createElement('div');
        el.className = 'city-ring-marker';
        el.style.width = `${totalSize}px`;
        el.style.height = `${totalSize}px`;
        el.style.cursor = 'pointer';
        
        // Create SVG for the stacked rings
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', String(totalSize));
        svg.setAttribute('height', String(totalSize));
        svg.style.display = 'block';
        
        // Add drop shadow
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
        filter.setAttribute('id', `shadow-${city.city}`);
        filter.setAttribute('x', '-50%');
        filter.setAttribute('y', '-50%');
        filter.setAttribute('width', '200%');
        filter.setAttribute('height', '200%');
        const feDropShadow = document.createElementNS('http://www.w3.org/2000/svg', 'feDropShadow');
        feDropShadow.setAttribute('dx', '0');
        feDropShadow.setAttribute('dy', '2');
        feDropShadow.setAttribute('stdDeviation', '3');
        feDropShadow.setAttribute('flood-opacity', '0.3');
        filter.appendChild(feDropShadow);
        defs.appendChild(filter);
        svg.appendChild(defs);
        
        const centerX = totalSize / 2;
        const centerY = totalSize / 2;
        
        // Draw rings from outside to inside
        let currentRadius = (totalSize / 2) - 2;
        
        sortedLayers.slice(0, 5).forEach((layer, index) => {
          if (layer.value <= 0) return;
          
          const thickness = Math.max(3, (layer.value / 10) * ringWidth * 1.5);
          const radius = currentRadius - thickness / 2;
          
          if (radius < 12) return;
          
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', String(centerX));
          circle.setAttribute('cy', String(centerY));
          circle.setAttribute('r', String(radius));
          circle.setAttribute('fill', 'none');
          circle.setAttribute('stroke', layer.color);
          circle.setAttribute('stroke-width', String(thickness));
          circle.setAttribute('opacity', layer.isElevated ? '0.9' : '0.5');
          
          svg.appendChild(circle);
          currentRadius -= thickness + 1;
        });
        
        // Center circle with city indicator
        const centerRadius = Math.max(14, currentRadius - 2);
        
        // Gradient-like center based on risk
        let centerColor = '#9E9E9E';
        if (maxRisk >= 8) centerColor = '#b71c1c';
        else if (maxRisk >= 6) centerColor = '#e65100';
        else if (maxRisk >= 4) centerColor = '#f9a825';
        else if (maxRisk >= 2) centerColor = '#558b2f';
        else centerColor = '#2e7d32';
        
        const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        bgCircle.setAttribute('cx', String(centerX));
        bgCircle.setAttribute('cy', String(centerY));
        bgCircle.setAttribute('r', String(centerRadius));
        bgCircle.setAttribute('fill', centerColor);
        bgCircle.setAttribute('filter', `url(#shadow-${city.city})`);
        svg.appendChild(bgCircle);
        
        // White border
        const borderCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        borderCircle.setAttribute('cx', String(centerX));
        borderCircle.setAttribute('cy', String(centerY));
        borderCircle.setAttribute('r', String(centerRadius));
        borderCircle.setAttribute('fill', 'none');
        borderCircle.setAttribute('stroke', 'white');
        borderCircle.setAttribute('stroke-width', '2');
        svg.appendChild(borderCircle);
        
        // Add center text INSIDE the same SVG (not a separate one)
        const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        textEl.setAttribute('x', String(centerX));
        textEl.setAttribute('y', String(centerY));
        textEl.setAttribute('text-anchor', 'middle');
        textEl.setAttribute('dominant-baseline', 'central');
        textEl.setAttribute('fill', 'white');
        textEl.setAttribute('font-size', '12');
        textEl.setAttribute('font-weight', 'bold');
        textEl.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
        textEl.textContent = String(city.count);
        svg.appendChild(textEl);
        
        el.appendChild(svg);
        
        // Tooltip
        el.title = `${city.city}, ${city.state}\n${city.count} postal codes\n${elevatedCount} elevated risks`;
        
        // Create popup HTML
        const popupHTML = `
          <div style="padding: 16px; min-width: 280px; font-family: system-ui, -apple-system, sans-serif;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
              <div>
                <div style="font-size: 18px; font-weight: bold; color: #1a1a1a;">${city.city}, ${city.state}</div>
                <div style="color: #666; font-size: 13px;">${city.count} postal codes</div>
              </div>
              <div style="background: ${centerColor}; color: white; padding: 8px 12px; border-radius: 8px; text-align: center;">
                <div style="font-size: 20px; font-weight: bold;">${maxRisk.toFixed(1)}</div>
                <div style="font-size: 10px; opacity: 0.9;">MAX RISK</div>
              </div>
            </div>
            
            <div style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); padding: 12px; border-radius: 8px; margin-bottom: 12px;">
              <div style="display: flex; justify-content: space-around; text-align: center;">
                <div>
                  <div style="font-size: 22px; font-weight: bold; color: ${elevatedCount >= 3 ? '#c62828' : elevatedCount > 0 ? '#ef6c00' : '#666'};">${elevatedCount}</div>
                  <div style="font-size: 11px; color: #666;">Elevated</div>
                </div>
                <div style="width: 1px; background: #ddd;"></div>
                <div>
                  <div style="font-size: 22px; font-weight: bold; color: #1976d2;">${avgRisk.toFixed(1)}</div>
                  <div style="font-size: 11px; color: #666;">Avg Risk</div>
                </div>
              </div>
            </div>
            
            <div style="font-size: 12px; font-weight: 600; color: #333; margin-bottom: 8px;">Hazard Summary (City Average)</div>
            <div style="max-height: 200px; overflow-y: auto;">
              ${sortedLayers.map(l => {
                const barWidth = Math.min(100, (l.value / 10) * 100);
                return `<div style="margin: 4px 0; padding: 6px; background: ${l.isElevated ? 'rgba(255,0,0,0.05)' : '#fafafa'}; border-radius: 4px; border-left: 3px solid ${l.color};">
                  <div style="display: flex; align-items: center; justify-content: space-between;">
                    <span style="font-size: 11px; color: ${l.isElevated ? '#333' : '#666'};">${l.name}</span>
                    <span style="font-size: 13px; font-weight: bold; color: ${l.color};">${l.value.toFixed(1)}</span>
                  </div>
                  <div style="height: 4px; background: #e0e0e0; border-radius: 2px; margin-top: 4px; overflow: hidden;">
                    <div style="height: 100%; width: ${barWidth}%; background: ${l.color};"></div>
                  </div>
                </div>`;
              }).join('')}
            </div>
            
            <div style="margin-top: 12px; text-align: center;">
              <span style="font-size: 11px; color: #1976d2; cursor: pointer;">Click to explore postal codes  
            </div>
          </div>
        `;
        
        // Create marker - simple element, default anchor works
        const marker = new mapboxgl.Marker(el)
          .setLngLat([avgLng, avgLat])
          .setPopup(
            new mapboxgl.Popup({ offset: 30, maxWidth: '320px' })
              .setHTML(popupHTML)
          );
        
        if (mapRef.current) {
          marker.addTo(mapRef.current);
        }
        
        el.addEventListener('click', () => handleSelectCity(city));
        markersRef.current.push(marker);
      });
    } else {
      // Postal code markers - Discovery (postal view or selected city) OR Portfolio mode
      const postalCodes = mode === 'discovery' && selectedCity 
        ? selectedCity.postalCodes 
        : filteredData;
      
      console.log(`Rendering ${postalCodes.length} postal code markers (source: ${mode === 'discovery' && selectedCity ? 'selectedCity' : 'filteredData'})`);
      
      // Get ALL available hazard layers for the stacked rings
      const allHazardLayers: DataLayer[] = ALL_LAYERS.slice(0, 8); // Use up to 8 layers for rings
      
      // Define type for layer values
      interface LayerValue {
        id: string;
        name: string;
        field: string;
        color: string;
        value: number;
        isElevated: boolean;
      }
      
      // ========== CREATE STACKED RING MARKERS ==========
      postalCodes.forEach(item => {
        // Parse and validate coordinates
        const lat = typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude as any);
        const lng = typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude as any);
        
        // Skip invalid coordinates
        if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return;
        
        // Calculate values for ALL hazard layers
        const layerValues: LayerValue[] = allHazardLayers.map((layer: DataLayer) => ({
          id: layer.id,
          name: layer.name,
          field: layer.field,
          color: layer.color,
          value: getFieldValue(item, layer.field),
          isElevated: getFieldValue(item, layer.field) >= 5 // Elevated if >= 5
        }));
        
        // Sort by value descending (highest risk on outside)
        const sortedLayers = [...layerValues].sort((a, b) => b.value - a.value);
        
        // Calculate overall risk score
        const avgRisk = layerValues.reduce((sum: number, l: LayerValue) => sum + l.value, 0) / layerValues.length;
        const maxRisk = Math.max(...layerValues.map((l: LayerValue) => l.value));
        const elevatedCount = layerValues.filter((l: LayerValue) => l.isElevated).length;
        
        // Base size for the marker
        const baseSize = 36;
        const ringWidth = 4; // Width of each ring
        const totalSize = baseSize + (sortedLayers.filter(l => l.value > 0).length * ringWidth * 2);
        
        // Create the marker container
        const el = document.createElement('div');
        el.className = 'stacked-ring-marker';
        el.style.width = `${totalSize}px`;
        el.style.height = `${totalSize}px`;
        el.style.cursor = 'pointer';
        
        // Create SVG for the stacked rings
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', String(totalSize));
        svg.setAttribute('height', String(totalSize));
        svg.style.display = 'block';
        
        const centerX = totalSize / 2;
        const centerY = totalSize / 2;
        
        // Draw rings from outside to inside (largest risk first)
        let currentRadius = (totalSize / 2) - 2;
        
        sortedLayers.forEach((layer, index) => {
          if (layer.value <= 0) return; // Skip zero values
          
          // Ring thickness based on value (0-10 scale)
          const thickness = Math.max(2, (layer.value / 10) * ringWidth * 1.5);
          const radius = currentRadius - thickness / 2;
          
          if (radius < 8) return; // Don't draw if too small
          
          // Create circle element
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', String(centerX));
          circle.setAttribute('cy', String(centerY));
          circle.setAttribute('r', String(radius));
          circle.setAttribute('fill', 'none');
          circle.setAttribute('stroke', layer.color);
          circle.setAttribute('stroke-width', String(thickness));
          circle.setAttribute('opacity', layer.isElevated ? '0.9' : '0.5');
          
          svg.appendChild(circle);
          currentRadius -= thickness + 1;
        });
        
        // Add center circle with overall risk indicator
        const centerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        const centerRadius = Math.max(10, currentRadius - 2);
        centerCircle.setAttribute('cx', String(centerX));
        centerCircle.setAttribute('cy', String(centerY));
        centerCircle.setAttribute('r', String(centerRadius));
        
        // Center color based on max risk
        let centerColor = '#9E9E9E';
        if (maxRisk >= 8) centerColor = '#c62828';
        else if (maxRisk >= 6) centerColor = '#ef6c00';
        else if (maxRisk >= 4) centerColor = '#fbc02d';
        else if (maxRisk >= 2) centerColor = '#7cb342';
        else centerColor = '#66bb6a';
        
        centerCircle.setAttribute('fill', centerColor);
        svg.appendChild(centerCircle);
        
        // Add white border around center
        const borderCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        borderCircle.setAttribute('cx', String(centerX));
        borderCircle.setAttribute('cy', String(centerY));
        borderCircle.setAttribute('r', String(centerRadius));
        borderCircle.setAttribute('fill', 'none');
        borderCircle.setAttribute('stroke', 'white');
        borderCircle.setAttribute('stroke-width', '2');
        svg.appendChild(borderCircle);
        
        // Add center text INSIDE the same SVG
        const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        textEl.setAttribute('x', String(centerX));
        textEl.setAttribute('y', String(centerY));
        textEl.setAttribute('text-anchor', 'middle');
        textEl.setAttribute('dominant-baseline', 'central');
        textEl.setAttribute('fill', 'white');
        textEl.setAttribute('font-size', '11');
        textEl.setAttribute('font-weight', 'bold');
        textEl.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
        textEl.textContent = elevatedCount > 0 ? String(elevatedCount) : '0';
        svg.appendChild(textEl);
        
        el.appendChild(svg);
        
        // Tooltip
        el.title = `${item.city}, ${item.state} (${item.postal_code})\n${elevatedCount} elevated risks\nMax: ${maxRisk.toFixed(1)}`;
        
        // Create comprehensive popup HTML
        const popupHTML = `
          <div style="padding: 16px; min-width: 280px; max-width: 320px; font-family: system-ui, -apple-system, sans-serif;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
              <div>
                <div style="font-size: 18px; font-weight: bold; color: #1a1a1a;">${item.postal_code}</div>
                <div style="color: #666; font-size: 14px;">${item.city}, ${item.state}</div>
              </div>
              <div style="background: ${centerColor}; color: white; padding: 8px 12px; border-radius: 8px; text-align: center;">
                <div style="font-size: 20px; font-weight: bold;">${maxRisk.toFixed(1)}</div>
                <div style="font-size: 10px; opacity: 0.9;">MAX RISK</div>
              </div>
            </div>
            
            <div style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); padding: 12px; border-radius: 8px; margin-bottom: 12px;">
              <div style="display: flex; justify-content: space-around; text-align: center;">
                <div>
                  <div style="font-size: 24px; font-weight: bold; color: ${elevatedCount >= 3 ? '#c62828' : elevatedCount > 0 ? '#ef6c00' : '#666'};">${elevatedCount}</div>
                  <div style="font-size: 11px; color: #666;">Elevated</div>
                </div>
                <div style="width: 1px; background: #ddd;"></div>
                <div>
                  <div style="font-size: 24px; font-weight: bold; color: #1976d2;">${avgRisk.toFixed(1)}</div>
                  <div style="font-size: 11px; color: #666;">Avg Risk</div>
                </div>
                <div style="width: 1px; background: #ddd;"></div>
                <div>
                  <div style="font-size: 24px; font-weight: bold; color: #7b1fa2;">${layerValues.length}</div>
                  <div style="font-size: 11px; color: #666;">Hazards</div>
                </div>
              </div>
            </div>
            
            <div style="font-size: 12px; font-weight: 600; color: #333; margin-bottom: 8px;">All Hazard Risks</div>
            <div style="max-height: 250px; overflow-y: auto;">
              ${sortedLayers.map(l => {
                const barWidth = Math.min(100, (l.value / 10) * 100);
                const riskLevel = l.value >= 8 ? 'EXTREME' : l.value >= 6 ? 'HIGH' : l.value >= 4 ? 'MODERATE' : l.value >= 2 ? 'LOW' : 'MINIMAL';
                return `<div style="margin: 6px 0; padding: 8px; background: ${l.isElevated ? 'rgba(255,0,0,0.05)' : '#fafafa'}; border-radius: 6px; border-left: 3px solid ${l.color};">
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                    <span style="font-size: 12px; font-weight: ${l.isElevated ? '600' : '400'}; color: ${l.isElevated ? '#333' : '#666'};">${l.name}</span>
                    <div style="display: flex; align-items: center; gap: 6px;">
                      <span style="font-size: 14px; font-weight: bold; color: ${l.color};">${l.value.toFixed(1)}</span>
                      ${l.isElevated ? '<span style="background: #c62828; color: white; font-size: 9px; padding: 2px 4px; border-radius: 3px;">!</span>' : ''}
                    </div>
                  </div>
                  <div style="display: flex; align-items: center; gap: 6px;">
                    <div style="flex: 1; height: 6px; background: #e0e0e0; border-radius: 3px; overflow: hidden;">
                      <div style="height: 100%; width: ${barWidth}%; background: ${l.color}; border-radius: 3px;"></div>
                    </div>
                    <span style="font-size: 9px; color: #999; min-width: 50px;">${riskLevel}</span>
                  </div>
                </div>`;
              }).join('')}
            </div>
            
            ${item.county ? `<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #eee; font-size: 11px; color: #888;">
              County: ${item.county}
            </div>` : ''}
          </div>
        `;
        
        // Create marker - simple element, default anchor works
        const marker = new mapboxgl.Marker(el)
          .setLngLat([lng, lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25, maxWidth: '350px' })
              .setHTML(popupHTML)
          );
        
        if (mapRef.current) {
          marker.addTo(mapRef.current);
        }
        
        // Click handler
        el.addEventListener('click', () => {
          setSelectedLocation(item);
          
          // Find the city this postal code belongs to and select it
          if (mode === 'discovery' && item.city && item.state) {
            const cityKey = `${item.city}, ${item.state}`;
            const city = cityAggregates.find(c => 
              `${c.city}, ${c.state}` === cityKey
            );
            if (city && (!selectedCity || selectedCity.city !== city.city || selectedCity.state !== city.state)) {
              setSelectedCity(city);
              drawCityBoundary(city);
            }
          }
        });
        markersRef.current.push(marker);
      });
      
      // Auto-zoom to show all markers if we have data
      if (postalCodes.length > 0 && mapRef.current && !hasUserMovedMap.current) {
        const bounds = new mapboxgl.LngLatBounds();
        let validCount = 0;
        
        postalCodes.forEach(item => {
          const lat = typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude as any);
          const lng = typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude as any);
          
          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            bounds.extend([lng, lat]);
            validCount++;
          }
        });
        
        if (validCount > 0 && !bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 12, duration: 1000 });
        }
      }
    }
  }, [selectedLayers, mode, viewMode, showHeatmap, heatmapIntensity, selectedCity, filteredCities, filteredData, cityAggregates, handleSelectCity, drawCityBoundary, fetchCityBoundaries, boundariesLoaded]);

  // Fullscreen keyboard handler (Escape to exit)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenMap) {
        setFullscreenMap(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenMap]);

  // Resize map when fullscreen changes
  useEffect(() => {
    if (mapRef.current) {
      // Small delay to allow CSS transition
      setTimeout(() => {
        mapRef.current?.resize();
      }, 100);
    }
  }, [fullscreenMap]);

  // Cleanup hazard layers when component unmounts or layers change
  useEffect(() => {
    return () => {
      if (mapRef.current && mapLoadedRef.current) {
        try {
          // Remove all hazard layers (fill, outline, and heat) BEFORE removing sources
          for (let i = 0; i < 20; i++) {
            const fillLayerId = `hazard-fill-${i}`;
            const outlineLayerId = `hazard-outline-${i}`;
            const heatLayerId = `hazard-heat-${i}`;
            const sourceId = `hazard-source-${i}`;
            
            // Remove all layers first (order matters!)
            if (mapRef.current.getLayer(fillLayerId)) {
              mapRef.current.removeLayer(fillLayerId);
            }
            if (mapRef.current.getLayer(outlineLayerId)) {
              mapRef.current.removeLayer(outlineLayerId);
            }
            if (mapRef.current.getLayer(heatLayerId)) {
              mapRef.current.removeLayer(heatLayerId);
            }
            // Now safe to remove the source
            if (mapRef.current.getSource(sourceId)) {
              mapRef.current.removeSource(sourceId);
            }
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [selectedLayers]);

  useEffect(() => {
    updateMarkers();
  }, [updateMarkers]);

  // =============== LAYER MANAGEMENT ===============

  const handleAddLayer = (layerId: string) => {
    const layerDef = getLayerById(layerId);
    if (!layerDef) return;
    
    if (!selectedLayers.find(l => l.layerId === layerId)) {
      setSelectedLayers([...selectedLayers, {
        layerId,
        visible: true,
        threshold: 0, // Start at 0 to show all data, user can adjust
        opacity: 0.7, // Default opacity
      }]);
    }
    setLayerDialogOpen(false);
  };

  const handleRemoveLayer = (layerId: string) => {
    setSelectedLayers(selectedLayers.filter(l => l.layerId !== layerId));
  };

  const handleToggleLayer = (layerId: string) => {
    setSelectedLayers(selectedLayers.map(l =>
      l.layerId === layerId ? { ...l, visible: !l.visible } : l
    ));
  };

  const handleThresholdChange = (layerId: string, threshold: number) => {
    setSelectedLayers(selectedLayers.map(l =>
      l.layerId === layerId ? { ...l, threshold } : l
    ));
  };

  const handleOpacityChange = (layerId: string, opacity: number) => {
    setSelectedLayers(selectedLayers.map(l =>
      l.layerId === layerId ? { ...l, opacity } : l
    ));
  };

  // =============== SEARCH ===============

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      // Clean up search query - handle "Boston, MA" format
      let cleanQuery = searchQuery.trim();
      // Remove commas and extra spaces, keep just the meaningful parts
      cleanQuery = cleanQuery.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
      
      const response = await axios.get(`${API_URL}/search/locations`, {
        params: { query: cleanQuery },
      });
      setSearchResults(response.data.results || []);
      console.log(`Search found ${response.data.results?.length || 0} results from API`);
    } catch (error) {
      console.error('Search error:', error);
      
      // In Discovery mode, we NEED the backend API - don't fall back to portfolio
      // In Portfolio mode, we can search within the loaded portfolio
      if (mode === 'portfolio' && allLocations.length > 0) {
        const query = searchQuery.toLowerCase();
        const matchingLocations = allLocations.filter(loc => 
          loc.city?.toLowerCase().includes(query) ||
          loc.state?.toLowerCase().includes(query) ||
          loc.postal_code?.toLowerCase().includes(query)
        );
        
        if (matchingLocations.length > 0) {
          // Group by city for city results
          const cityMap = new Map<string, PortfolioItem[]>();
          matchingLocations.forEach(loc => {
            const key = `${loc.city}, ${loc.state}`;
            if (!cityMap.has(key)) cityMap.set(key, []);
            cityMap.get(key)!.push(loc);
          });
          
          const results: any[] = [];
          
          // Add city results (grouped)
          cityMap.forEach((locations, cityKey) => {
            const [city, state] = cityKey.split(', ');
            const avgRisk = locations.reduce((sum, l) => sum + (l.risk_score || 0), 0) / locations.length;
            results.push({
              search_type: 'city',
              city,
              state,
              risk_score: avgRisk,
              locations,
            });
          });
          
          setSearchResults(results.slice(0, 10));
          console.log(`Mock search found ${results.length} cities with ${matchingLocations.length} total locations in portfolio`);
        } else {
          setSearchResults([]);
          console.log(`No results found for "${searchQuery}" in current portfolio`);
        }
      } else {
        // Discovery mode or no portfolio - can't search without backend
        setSearchResults([]);
        console.log(`Search requires backend API. Mode: ${mode}, Portfolio loaded: ${allLocations.length > 0}`);
      }
    } finally {
      setSearching(false);
    }
  };

  const addSearchResult = (result: SearchResult) => {
    if (result.search_type === 'city' && result.locations) {
      // Add city locations to existing data (supporting multiple cities)
      const newData = [...allLocations];
      result.locations.forEach(loc => {
        if (!newData.find(d => d.postal_code === loc.postal_code)) {
          newData.push(loc);
        }
      });
      setAllLocations(newData);
      setLoadedCities([...loadedCities, `${result.city}, ${result.state}`]);
      
      // Fly to the city
      if (mapRef.current && result.latitude && result.longitude) {
        mapRef.current.flyTo({
          center: [result.longitude, result.latitude],
          zoom: 11,
          duration: 1500
        });
      } else if (mapRef.current && result.locations.length > 0) {
        // Calculate bounds from all locations
        const bounds = new mapboxgl.LngLatBounds();
        result.locations.forEach(loc => {
          if (loc.latitude && loc.longitude) {
            bounds.extend([loc.longitude, loc.latitude]);
          }
        });
        if (!bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, { padding: 50, duration: 1500 });
        }
      }
      
      setSearchDialogOpen(false);
    } else {
      // Single postal code
      if (!allLocations.find(d => d.postal_code === result.postal_code)) {
        setAllLocations([...allLocations, result as any]);
      }
      
      // Fly to the location
      if (mapRef.current && result.latitude && result.longitude) {
        mapRef.current.flyTo({
          center: [result.longitude, result.latitude],
          zoom: 13,
          duration: 1500
        });
      }
      
      setSearchDialogOpen(false);
    }
  };

  // =============== SCENARIO MANAGEMENT (Portfolio Mode) ===============

  const saveScenario = () => {
    const newScenario: Scenario = {
      id: `scenario_${Date.now()}`,
      name: `Scenario ${scenarios.length}`,
      items: filteredData,
      totalValue: filteredData.reduce((sum, i) => sum + getFieldValue(i, 'asset_value'), 0),
      avgRisk: filteredData.reduce((sum, i) => sum + getFieldValue(i, 'risk_score'), 0) / (filteredData.length || 1),
      propertyCount: filteredData.length,
      modifications: selectedLayers.map(l => {
        const layerDef = getLayerById(l.layerId);
        return `${layerDef?.name} >= ${l.threshold}`;
      }),
    };
    
    setScenarios([...scenarios, newScenario]);
    setScenarioDialogOpen(false);
  };

  // =============== FILTER MANAGEMENT (Portfolio Mode) ===============

  const addFilter = () => {
    setFilters([...filters, {
      field: 'risk_score',
      operator: '>=',
      value: 5,
      enabled: true,
    }]);
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const updateFilter = (index: number, updates: Partial<FilterCondition>) => {
    setFilters(filters.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  // =============== BUILD PORTFOLIO ===============

  const toggleBuildMode = () => {
    if (buildMode) {
      // Exiting build mode
      if (portfolioItems.length > 0) {
        // Show save dialog if there are items
        setPortfolioDialogOpen(true);
      } else {
        setBuildMode(false);
      }
    } else {
      // Entering build mode
      setBuildMode(true);
      setPortfolioItems([]);
    }
  };

  const openSavePortfolioDialog = () => {
    if (portfolioItems.length > 0) {
      setPortfolioDialogOpen(true);
    }
  };

  const cancelBuildMode = () => {
    setBuildMode(false);
    setPortfolioItems([]);
    setNewPortfolioName('');
  };

  // Add locations from current map view to workflow
  const addCurrentViewToWorkflow = async () => {
    if (!mapRef.current) return;
    
    setLoading(true);
    try {
      const bounds = mapRef.current.getBounds();
      if (!bounds) {
        console.log('Could not get map bounds');
        setLoading(false);
        return;
      }
      
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      
      console.log(`Adding current view: SW(${sw.lat.toFixed(4)}, ${sw.lng.toFixed(4)}) to NE(${ne.lat.toFixed(4)}, ${ne.lng.toFixed(4)})`);
      
      // Build layer criteria for the search
      const visibleLayers = selectedLayers.filter(l => l.visible);
      const layers = visibleLayers.map(layer => {
        const layerDef = getLayerById(layer.layerId);
        return {
          field: layerDef?.field || '',
          threshold: layer.threshold
        };
      }).filter(l => l.field);
      
      // Search for locations in current view bounds with layer criteria
      const response = await axios.post(`${API_URL}/search/by-hazards`, {
        layers: layers.length > 0 ? layers : [{ field: 'risk', threshold: 0 }],
        overlapMode: overlapMode === 'AND',
        limit: 1000,
        bounds: {
          minLat: sw.lat,
          maxLat: ne.lat,
          minLng: sw.lng,
          maxLng: ne.lng
        }
      });
      
      const newLocations = response.data.results || [];
      
      // Filter to only locations within bounds (in case backend doesn't support bounds)
      const inBoundsLocations = newLocations.filter((loc: any) => {
        const lat = parseFloat(loc.latitude);
        const lng = parseFloat(loc.longitude);
        return lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng;
      });
      
      // Add to existing locations
      const existingCodes = new Set(allLocations.map(l => l.postal_code));
      const uniqueNew = inBoundsLocations.filter((loc: any) => !existingCodes.has(loc.postal_code));
      
      if (uniqueNew.length > 0) {
        setAllLocations([...allLocations, ...uniqueNew]);
        console.log(`Added ${uniqueNew.length} new locations from current view`);
      } else {
        console.log('No new locations to add from current view');
      }
      
      // Track the area
      const areaName = `Area ${loadedCities.length + 1}`;
      setLoadedCities([...loadedCities, areaName]);
      
    } catch (error) {
      console.error('Error adding current view:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBuildPortfolio = async () => {
    if (!newPortfolioName.trim() || portfolioItems.length === 0) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 'demo';
      
      // Save to backend
      const response = await axios.post(
        `${API_URL}/portfolios`,
        {
          name: newPortfolioName,
          description: `Portfolio created from Map (${portfolioItems.length} locations)`,
          userId: userId,
          items: portfolioItems.map(item => ({
            postalCode: item.postal_code,
            postal_code: item.postal_code,
            city: item.city,
            state: item.state,
            assetValue: parseFloat(String(item.asset_value)) || 0,
            asset_value: parseFloat(String(item.asset_value)) || 0,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Portfolio saved:', response.data);
      
      // Also save to localStorage for immediate access on portfolio page
      const portfolioData = {
        id: response.data.id || `map_${Date.now()}`,
        name: newPortfolioName,
        items: portfolioItems,
        locations: portfolioItems,
        total_value: portfolioItems.reduce((sum, item) => sum + (parseFloat(String(item.asset_value)) || 0), 0),
        avg_risk: portfolioItems.reduce((sum, item) => sum + (item.risk_score || 0), 0) / portfolioItems.length,
        item_count: portfolioItems.length,
        created_at: new Date().toISOString(),
      };
      
      localStorage.setItem('lastMappedPortfolio', JSON.stringify(portfolioData));
      
      setPortfolioDialogOpen(false);
      setNewPortfolioName('');
      setBuildMode(false);
      setPortfolioItems([]);
      
      // Navigate to portfolio page
      navigate('/portfolio');
      
    } catch (error: any) {
      console.error('Save portfolio error:', error);
      
      // Even if API fails, save locally and navigate
      const portfolioData = {
        id: `local_${Date.now()}`,
        name: newPortfolioName,
        items: portfolioItems,
        locations: portfolioItems,
        total_value: portfolioItems.reduce((sum, item) => sum + (parseFloat(String(item.asset_value)) || 0), 0),
        avg_risk: portfolioItems.reduce((sum, item) => sum + (item.risk_score || 0), 0) / portfolioItems.length,
        item_count: portfolioItems.length,
        created_at: new Date().toISOString(),
      };
      
      localStorage.setItem('lastMappedPortfolio', JSON.stringify(portfolioData));
      
      setPortfolioDialogOpen(false);
      setNewPortfolioName('');
      setBuildMode(false);
      setPortfolioItems([]);
      
      // Navigate to portfolio page even on error (will use local data)
      navigate('/portfolio');
    } finally {
      setLoading(false);
    }
  };

  // =============== EXPORT ===============

  const exportResults = () => {
    const dataToExport = mode === 'discovery' && viewMode === 'cities' ? filteredCities : filteredData;
    
    if (mode === 'discovery' && viewMode === 'cities') {
      const csvRows = [
        ['City', 'State', 'Postal Code Count', 'Avg Risk', ...selectedLayers.map(l => getLayerById(l.layerId)?.name || l.layerId)],
        ...filteredCities.map(city => [
          city.city,
          city.state,
          city.count.toString(),
          city.avgRisk.toFixed(2),
          ...selectedLayers.map(l => {
            const layerDef = getLayerById(l.layerId);
            return layerDef ? (city.layerValues[layerDef.field] || 0).toFixed(2) : '';
          }),
        ]),
      ];
      
      const csv = csvRows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map_cities_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else {
      const csvRows = [
        ['Postal Code', 'City', 'State', 'Latitude', 'Longitude', 'Risk Score', ...selectedLayers.map(l => getLayerById(l.layerId)?.name || l.layerId)],
        ...filteredData.map(item => [
          item.postal_code || '',
          item.city || '',
          item.state || '',
          item.latitude?.toString() || '',
          item.longitude?.toString() || '',
          (item.risk_score || 0).toFixed(2),
          ...selectedLayers.map(l => {
            const layerDef = getLayerById(l.layerId);
            return layerDef ? getFieldValue(item, layerDef.field).toFixed(2) : '';
          }),
        ]),
      ];
      
      const csv = csvRows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `map_locations_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  };

  // =============== STATISTICS ===============

  const stats = useMemo(() => {
    const displayData: (CityAggregate | PortfolioItem)[] = mode === 'discovery' && viewMode === 'cities' 
      ? filteredCities
      : filteredData;
    
    const avgRisk = displayData.length > 0
      ? displayData.reduce((sum, item) => {
          return sum + ('avgRisk' in item ? item.avgRisk : (item.risk_score || 0));
        }, 0) / displayData.length
      : 0;
    
    const highRisk = displayData.filter((item) => {
      const risk = 'avgRisk' in item ? item.avgRisk : (item.risk_score || 0);
      return risk >= 7;
    }).length;
    
    const medRisk = displayData.filter((item) => {
      const risk = 'avgRisk' in item ? item.avgRisk : (item.risk_score || 0);
      return risk >= 4 && risk < 7;
    }).length;
    
    const lowRisk = displayData.length - highRisk - medRisk;

    return {
      total: displayData.length,
      avgRisk,
      highRisk,
      medRisk,
      lowRisk,
    };
  }, [filteredCities, filteredData, mode, viewMode]);

  // =============== RENDER FUNCTIONS ===============

  const renderLayerControls = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Data Layers ({selectedLayers.length}/37)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select hazards, environmental, demographic, or financial layers
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setLayerDialogOpen(true)}
          variant="contained"
        >
          Add Layer
        </Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Overlap Mode
          </Typography>
          <ToggleButtonGroup
            value={overlapMode}
            exclusive
            onChange={(_, val) => val && setOverlapMode(val)}
            size="small"
          >
            <ToggleButton value="AND">
              AND
            </ToggleButton>
            <ToggleButton value="OR">
              OR
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {selectedLayers.map(layer => {
        const layerDef = getLayerById(layer.layerId);
        if (!layerDef) return null;

        return (
          <Accordion key={layer.layerId} defaultExpanded={layer.visible}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Box sx={{ color: layerDef.color }}>{layerDef.icon}</Box>
                <Typography sx={{ fontWeight: 'bold', flex: 1 }}>{layerDef.name}</Typography>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleLayer(layer.layerId);
                  }}
                >
                  {layer.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLayer(layer.layerId);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {layerDef.description}
              </Typography>
              
              {/* Opacity Control */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: 1,
                    background: `linear-gradient(90deg, transparent, ${layerDef.color})`,
                    border: '1px solid #ddd'
                  }} />
                  Opacity: {Math.round((layer.opacity ?? 0.7) * 100)}%
                </Typography>
                <Slider
                  value={layer.opacity ?? 0.7}
                  onChange={(_, val) => handleOpacityChange(layer.layerId, val as number)}
                  min={0.1}
                  max={1}
                  step={0.1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                  sx={{ 
                    mt: 1,
                    color: layerDef.color,
                    '& .MuiSlider-thumb': { bgcolor: layerDef.color },
                    '& .MuiSlider-track': { bgcolor: layerDef.color },
                  }}
                />
              </Box>
              
              {/* Threshold Control */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  Threshold: &gt;= {layer.threshold.toFixed(1)}
                </Typography>
                <Slider
                  value={layer.threshold}
                  onChange={(_, val) => handleThresholdChange(layer.layerId, val as number)}
                  min={layerDef.scale.min}
                  max={layerDef.scale.max}
                  step={(layerDef.scale.max - layerDef.scale.min) / 100}
                  valueLabelDisplay="auto"
                  sx={{ mt: 1 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {selectedLayers.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Click "Add Layer" to begin analysis
          </Typography>
        </Alert>
      )}
    </Paper>
  );


  // =============== MAIN RENDER ===============

  return (
    <Container maxWidth="xl" sx={{ py: fullscreenMap ? 0 : 2 }}>
      {/* Header - hidden in fullscreen */}
      {!fullscreenMap && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Climate Risk Map
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, val) => val && setMode(val)}
            size="small"
          >
            <ToggleButton value="discovery">Discovery</ToggleButton>
            <ToggleButton value="portfolio">Portfolio</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Mode Indicator Banner - hidden in fullscreen */}
      {!fullscreenMap && (
        <Alert 
          severity={mode === 'portfolio' ? 'success' : 'info'} 
          sx={{ mb: 2, py: 0.5 }}
          icon={mode === 'portfolio' ? <FolderOpenIcon /> : <SearchIcon />}
        >
          <strong>{mode === 'portfolio' ? 'Portfolio Mode' : 'Discovery Mode'}</strong>
          {' - '}
          {mode === 'portfolio' ? (
            allLocations.length > 0 ? (
              <span>Analyzing {allLocations.length} locations from your portfolio</span>
            ) : (
              <span style={{ color: '#d32f2f' }}> No portfolio loaded. Go to Portfolio page to Map a portfolio!</span>
            )
          ) : (
            <span>Search and explore locations. Switch to Portfolio mode to analyze your portfolio.</span>
          )}
        </Alert>
      )}

      {/* Loading indicator - hidden in fullscreen */}
      {!fullscreenMap && loading && (
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
          {mode === 'discovery' ? 'Searching for matching locations...' : 'Loading portfolio...'}
        </Alert>
      )}

      {/* Selected City Banner with Hazard Value - hidden in fullscreen */}
      {selectedCity && !fullscreenMap && (
        <Paper 
          elevation={2}
          sx={{ 
            mb: 2, 
            p: 2, 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
            borderLeft: `4px solid ${(() => {
              const visibleLayers = selectedLayers.filter(l => l.visible);
              const primaryLayer = visibleLayers.length > 0 ? getLayerById(visibleLayers[0].layerId) : null;
              return primaryLayer?.color || '#1976d2';
            })()}`
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {selectedCity.city}, {selectedCity.state}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCity.count} postal codes in this city
              </Typography>
            </Box>
            
            {/* Hazard Value Display */}
            {(() => {
              const visibleLayers = selectedLayers.filter(l => l.visible);
              const primaryLayer = visibleLayers.length > 0 ? getLayerById(visibleLayers[0].layerId) : null;
              
              if (primaryLayer && selectedCity.postalCodes.length > 0) {
                let total = 0;
                let min = Infinity;
                let max = -Infinity;
                selectedCity.postalCodes.forEach(pc => {
                  const val = getFieldValue(pc, primaryLayer.field);
                  total += val;
                  min = Math.min(min, val);
                  max = Math.max(max, val);
                });
                const avg = total / selectedCity.postalCodes.length;
                const isElevated = avg >= (visibleLayers[0].threshold || 5);
                
                return (
                  <Box sx={{ 
                    textAlign: 'center', 
                    bgcolor: isElevated ? primaryLayer.color : '#e0e0e0',
                    color: isElevated ? 'white' : '#666',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    minWidth: 100,
                    ml: 2
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                      {avg.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {primaryLayer.name}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.7, fontSize: '10px' }}>
                      Range: {min.toFixed(1)} - {max.toFixed(1)}
                    </Typography>
                  </Box>
                );
              }
              return null;
            })()}
            
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleBackToCities}
              startIcon={<ArrowBackIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Box>
        </Paper>
      )}

      {/* Stats - hidden in fullscreen */}
      {!fullscreenMap && (
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={3}>
          <Card>
            <CardContent sx={{ py: 1, px: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {mode === 'discovery' && viewMode === 'cities' ? 'Cities' : 'Locations'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent sx={{ py: 1, px: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Avg Risk</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {stats.avgRisk.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
            <CardContent sx={{ py: 1, px: 1.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>High </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{stats.highRisk}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent sx={{ py: 1, px: 1.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Med (4-7)</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{stats.medRisk}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      <Grid container spacing={fullscreenMap ? 0 : 2}>
        {/* Left Panel - Controls (visible in fullscreen as overlay) */}
        <Grid 
          item 
          xs={12} 
          md={fullscreenMap ? 12 : 2}
          sx={fullscreenMap ? {
            position: 'fixed',
            top: 70,
            left: 16,
            zIndex: 10000,
            width: '220px !important',
            maxWidth: '220px',
            maxHeight: 'calc(100vh - 90px)',
            overflowY: 'auto',
          } : {}}
        >
          {renderLayerControls()}
          
          {/* Discovery Mode Controls */}
          {mode === 'discovery' && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                View Mode
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => val && setViewMode(val)}
                fullWidth
                size="small"
              >
                <ToggleButton value="cities">
                  <LocationCityIcon sx={{ mr: 0.5 }} /> Cities
                </ToggleButton>
                <ToggleButton value="postal">
                  <LocationOnIcon sx={{ mr: 0.5 }} /> Postal
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setSearchDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Search Locations
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={addCurrentViewToWorkflow}
                sx={{ mt: 1 }}
              >
                Add Current View
              </Button>
              
              {/* Build Portfolio Section */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: buildMode ? 'primary.main' : 'text.secondary' }}>
                {buildMode ? `Building Portfolio (${portfolioItems.length} locations)` : 'Build Portfolio'}
              </Typography>
              
              {!buildMode ? (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BuildIcon />}
                  onClick={toggleBuildMode}
                  sx={{ mt: 1 }}
                >
                  Start Building
                </Button>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                    Click anywhere on the map to add locations
                  </Alert>
                  
                  {portfolioItems.length > 0 && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {portfolioItems.length} location{portfolioItems.length !== 1 ? 's' : ''} selected
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Total Value: ${portfolioItems.reduce((sum, item) => sum + (parseFloat(String(item.asset_value)) || 0), 0).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={openSavePortfolioDialog}
                      disabled={portfolioItems.length === 0}
                      sx={{ flex: 1 }}
                    >
                      Save ({portfolioItems.length})
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={cancelBuildMode}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      <CloseIcon />
                    </Button>
                  </Box>
                </Box>
              )}
              
              {/* Show loaded cities/areas */}
              {loadedCities.length > 0 && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                    Loaded Areas ({loadedCities.length}):
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {loadedCities.map((city, i) => (
                      <Chip
                        key={i}
                        label={city}
                        size="small"
                        onDelete={() => {
                          // Remove this city/area's locations
                          setLoadedCities(loadedCities.filter((_, idx) => idx !== i));
                        }}
                        sx={{ fontSize: '10px' }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Total: {allLocations.length} locations
                  </Typography>
                </Box>
              )}
              
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setAllLocations([]);
                  setSelectedLayers([]);
                  setLoadedCities([]);
                  setSelectedCity(null);
                  setSelectedLocation(null);
                  setBuildMode(false);
                  setPortfolioItems([]);
                  clearCityBoundary();
                  clearMapData().catch(err => console.error('Error clearing map data:', err));
                  localStorage.removeItem('lastMappedPortfolio'); // Legacy cleanup
                  // Reset map to US view
                  if (mapRef.current) {
                    mapRef.current.flyTo({
                      center: [-98.5795, 39.8283],
                      zoom: 4,
                      duration: 1000
                    });
                  }
                  hasUserMovedMap.current = false;
                  console.log('Cleared all data and layers');
                }}
                sx={{ mt: 2 }}
              >
                Clear All
              </Button>
            </Paper>
          )}

          {/* Portfolio Mode Controls */}
          {mode === 'portfolio' && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Portfolio Tools
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => setFilterDialogOpen(true)}
                sx={{ mb: 1 }}
              >
                Advanced Filters ({filters.filter(f => f.enabled).length})
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setAllLocations([]);
                  setSelectedLayers([]);
                  setScenarios([]);
                  setCurrentScenario('baseline');
                  clearMapData().catch(err => console.error('Error clearing map data:', err));
                  localStorage.removeItem('lastMappedPortfolio'); // Legacy cleanup
                  console.log('Cleared all portfolio data and layers');
                }}
                sx={{ mt: 1 }}
              >
                Clear All
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<BookmarkIcon />}
                onClick={() => setScenarioDialogOpen(true)}
                disabled={filteredData.length === 0}
              >
                Save Scenario
              </Button>
              {scenarios.length > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Scenarios:
                  </Typography>
                  {scenarios.map(s => (
                    <Chip
                      key={s.id}
                      label={s.name}
                      size="small"
                      color={s.id === currentScenario ? 'primary' : 'default'}
                      onClick={() => setCurrentScenario(s.id)}
                      sx={{ mt: 0.5, mr: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          )}

          {/* Selected Location Details */}
          {selectedLocation && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Location Details
                </Typography>
                <IconButton size="small" onClick={() => setSelectedLocation(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="body2">
                <strong>Postal Code:</strong> {selectedLocation.postal_code}<br/>
                <strong>City:</strong> {selectedLocation.city}, {selectedLocation.state}<br/>
                <strong>Risk Score:</strong> {(selectedLocation.risk_score || 0).toFixed(1)}<br/>
              </Typography>
              {selectedLayers.filter(l => l.visible).map(layer => {
                const layerDef = getLayerById(layer.layerId);
                if (!layerDef) return null;
                const value = getFieldValue(selectedLocation, layerDef.field);
                return (
                  <Box key={layer.layerId} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {layerDef.name}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: layerDef.color }}>
                      {value.toFixed(1)}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          )}
        </Grid>

        {/* Right Panel - Map (full width in fullscreen) */}
        <Grid item xs={12} md={fullscreenMap ? 12 : 10}>
          <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: fullscreenMap ? 0 : 2 }}>
            {/* Map Legend - hidden in fullscreen */}
            {!fullscreenMap && (
            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Active Layers:
                  </Typography>
                  {selectedLayers.filter(l => l.visible).map(layer => {
                    const layerDef = getLayerById(layer.layerId);
                    if (!layerDef) return null;
                    const opacity = layer.opacity ?? 0.7;
                    
                    return (
                      <Tooltip 
                        key={layer.layerId}
                        title={`${layerDef.name}: Threshold ${layer.threshold.toFixed(1)}, Opacity ${Math.round(opacity * 100)}%`}
                      >
                        <Chip
                          size="small"
                          icon={
                            <Box sx={{ 
                              width: 16, 
                              height: 16, 
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${layerDef.color}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 0%, ${layerDef.color} 100%)`,
                              border: '2px solid white',
                              ml: 0.5,
                            }} />
                          }
                          label={`${layerDef.name} ${layer.threshold.toFixed(1)}`}
                          onDelete={() => handleRemoveLayer(layer.layerId)}
                          sx={{ 
                            bgcolor: `${layerDef.color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`,
                            color: 'white',
                            fontWeight: 'bold',
                            '& .MuiChip-label': { px: 1 },
                            '& .MuiChip-deleteIcon': { color: 'white' },
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                  {selectedLayers.filter(l => l.visible).length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No layers active - add layers from left panel
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Heatmap Controls */}
                  {selectedLayers.filter(l => l.visible).length >= 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', px: 1.5, py: 0.5, borderRadius: 1 }}>
                      <Tooltip title="Toggle heatmap overlay">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={showHeatmap}
                              onChange={(e) => setShowHeatmap(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Heatmap
                            </Typography>
                          }
                          sx={{ mr: 0 }}
                        />
                      </Tooltip>
                      {showHeatmap && (
                        <Tooltip title="Adjust global heatmap intensity">
                          <Box sx={{ width: 80, mx: 1 }}>
                            <Slider
                              size="small"
                              value={heatmapIntensity}
                              onChange={(_, val) => setHeatmapIntensity(val as number)}
                              min={0.2}
                              max={1.0}
                              step={0.1}
                              sx={{ py: 0 }}
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportResults}
                    disabled={!stats.total}
                  >
                    Export
                  </Button>
                </Box>
              </Box>
            </Box>
            )}
            
            {/* Map Container */}
            <Box 
              ref={mapContainerRef}
              sx={{ 
                height: fullscreenMap ? '100vh' : 'calc(100vh - 220px)',
                minHeight: fullscreenMap ? '100vh' : '600px',
                bgcolor: '#e8e8e8',
                position: fullscreenMap ? 'fixed' : 'relative',
                top: fullscreenMap ? 0 : 'auto',
                left: fullscreenMap ? 0 : 'auto',
                right: fullscreenMap ? 0 : 'auto',
                bottom: fullscreenMap ? 0 : 'auto',
                zIndex: fullscreenMap ? 9999 : 1,
                width: fullscreenMap ? '100vw' : '100%',
              }}
            >
              {/* Fullscreen Toggle Button - Top Right */}
              <Tooltip title={fullscreenMap ? "Exit Fullscreen (Esc)" : "Fullscreen"}>
                <IconButton
                  onClick={() => setFullscreenMap(!fullscreenMap)}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 10,
                    bgcolor: 'white',
                    color: 'text.primary',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    boxShadow: 2,
                  }}
                >
                  {fullscreenMap ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
              
              {/* Reset View Button - Top Left */}
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (mapRef.current) {
                    // Clear selections
                    setSelectedCity(null);
                    setSelectedLocation(null);
                    clearCityBoundary();
                    hasUserMovedMap.current = false;
                    
                    // Fly to US view
                    mapRef.current.flyTo({
                      center: [-98.5795, 39.8283],
                      zoom: 4,
                      duration: 1000
                    });
                  }
                }}
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 10,
                  bgcolor: 'white',
                  color: 'text.primary',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  boxShadow: 2,
                }}
                startIcon={<MapIcon />}
              >
                Reset View
              </Button>
              
              {/* Floating Hazard Legend - Choropleth Style */}
              {showHeatmap && selectedLayers.filter(l => l.visible).length > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    bottom: 40,
                    right: 16,
                    zIndex: 5,
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(4px)',
                    minWidth: 180,
                    maxWidth: 220,
                    boxShadow: 3,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                    Hazard Intensity
                  </Typography>
                  
                  {/* Risk Scale Legend - Light to Dark like reference image */}
                  <Box sx={{ mb: 2 }}>
                    {/* Color scale boxes like the reference image */}
                    {selectedLayers.filter(l => l.visible).slice(0, 1).map(layer => {
                      const layerDef = getLayerById(layer.layerId);
                      if (!layerDef) return null;
                      const baseColor = layerDef.color;
                      // Parse hex to RGB
                      const r = parseInt(baseColor.slice(1, 3), 16);
                      const g = parseInt(baseColor.slice(3, 5), 16);
                      const b = parseInt(baseColor.slice(5, 7), 16);
                      
                      return (
                        <Box key={layer.layerId}>
                          {[
                            { label: 'Very Low', color: `rgb(${Math.min(255, r + 180)}, ${Math.min(255, g + 180)}, ${Math.min(255, b + 180)})` },
                            { label: 'Low', color: `rgb(${Math.min(255, r + 120)}, ${Math.min(255, g + 120)}, ${Math.min(255, b + 120)})` },
                            { label: 'Medium', color: `rgb(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)})` },
                            { label: 'High', color: baseColor },
                            { label: 'Very High', color: `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})` },
                          ].map((item, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{ 
                                width: 20, 
                                height: 14, 
                                bgcolor: item.color,
                                border: '1px solid #ccc',
                                borderRadius: 0.5,
                              }} />
                              <Typography variant="caption" sx={{ fontSize: 10 }}>{item.label}</Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                    
                    {/* Show multi-layer note when multiple layers active */}
                    {selectedLayers.filter(l => l.visible).length > 1 && (
                      <Box sx={{ 
                        display: 'flex', 
                        height: 16, 
                        borderRadius: 1, 
                        overflow: 'hidden',
                        border: '1px solid #ddd',
                        mb: 1,
                      }}>
                        {selectedLayers.filter(l => l.visible).map((layer, i) => {
                          const layerDef = getLayerById(layer.layerId);
                          return (
                            <Box 
                              key={i} 
                              sx={{ 
                                flex: 1, 
                                bgcolor: layerDef?.color,
                                opacity: layer.opacity ?? 0.7,
                              }} 
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  
                  {/* Active Layers - differentiate base vs overlay */}
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'text.secondary' }}>
                    Active Layers ({selectedLayers.filter(l => l.visible).length})
                  </Typography>
                  {selectedLayers.filter(l => l.visible).map((layer, idx) => {
                    const layerDef = getLayerById(layer.layerId);
                    if (!layerDef) return null;
                    const opacity = layer.opacity ?? 0.7;
                    const isBaseLayer = idx === 0;
                    
                    return (
                      <Box key={layer.layerId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        {/* Different swatch for base layer vs overlay */}
                        {isBaseLayer ? (
                          // Base layer - solid gradient swatch
                          <Box sx={{
                            width: 40,
                            height: 12,
                            borderRadius: 0.5,
                            background: `linear-gradient(90deg, ${layerDef.color}40 0%, ${layerDef.color}80 50%, ${layerDef.color} 100%)`,
                            opacity: opacity,
                            border: '1px solid #ddd',
                          }} />
                        ) : (
                          // Overlay - circular heat indicator
                          <Box sx={{
                            width: 40,
                            height: 12,
                            borderRadius: 6,
                            background: `radial-gradient(circle, ${layerDef.color} 30%, ${layerDef.color}60 60%, transparent 100%)`,
                            opacity: opacity,
                            border: '1px solid #ddd',
                          }} />
                        )}
                        <Typography variant="caption" sx={{ flex: 1, fontSize: 11, fontWeight: 500 }}>
                          {layerDef.name}
                        </Typography>
                        <Chip 
                          label={isBaseLayer ? 'Base' : 'Overlay'} 
                          size="small" 
                          sx={{ 
                            height: 16, 
                            fontSize: 8, 
                            bgcolor: isBaseLayer ? '#e3f2fd' : '#fff3e0',
                            color: isBaseLayer ? '#1565c0' : '#e65100',
                          }} 
                        />
                        <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', bgcolor: '#f5f5f5', px: 0.5, borderRadius: 0.5 }}>
                          {Math.round(opacity * 100)}%
                        </Typography>
                      </Box>
                    );
                  })}
                  
                  {selectedLayers.filter(l => l.visible).length > 1 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #eee' }}>
                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', display: 'block' }}>
                       <strong>Tip:</strong> Overlapping colors indicate compound risk areas. Adjust layer opacity to explore relationships.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
              
              {/* Multi-Hazard Stats Panel */}
              {showHeatmap && selectedLayers.filter(l => l.visible).length > 1 && stats.total > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    bottom: 40,
                    left: 16,
                    zIndex: 5,
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(4px)',
                    boxShadow: 3,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Multi-Hazard Exposure
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[1, 2, 3].map(count => {
                      const visibleLayers = selectedLayers.filter(l => l.visible);
                      const matchingCount = filteredData.filter(item => {
                        const exceededCount = visibleLayers.filter(layer => {
                          const layerDef = getLayerById(layer.layerId);
                          if (!layerDef) return false;
                          const value = getFieldValue(item, layerDef.field);
                          return value >= layer.threshold;
                        }).length;
                        return exceededCount >= count;
                      }).length;
                      
                      const color = count >= 3 ? '#d32f2f' : count >= 2 ? '#ff9800' : '#4caf50';
                      
                      return (
                        <Box key={count} sx={{ textAlign: 'center', minWidth: 50 }}>
                          <Typography sx={{ 
                            fontSize: 20, 
                            fontWeight: 'bold', 
                            color: color,
                          }}>
                            {matchingCount}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9 }}>
                            {count}+ hazards
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              )}
              
              {(!MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token') && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5',
                  zIndex: 10,
                }}>
                  <MapIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Map Configuration Required
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                    Please add your Mapbox token to the environment variables (REACT_APP_MAPBOX_TOKEN).
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* City Results Table (Discovery Mode) */}
          {mode === 'discovery' && viewMode === 'cities' && !selectedCity && filteredCities.length > 0 && (
            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                City Results ({filteredCities.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>City, State</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Postal Codes</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Avg Risk</TableCell>
                      {selectedLayers.filter(l => l.visible).map(layer => {
                        const layerDef = getLayerById(layer.layerId);
                        return (
                          <TableCell key={layer.layerId} align="center" sx={{ fontWeight: 'bold' }}>
                            {layerDef?.name}
                          </TableCell>
                        );
                      })}
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCities.slice(0, 50).map((city, index) => (
                      <TableRow 
                        key={`${city.city}-${city.state}-${index}`}
                        hover
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell onClick={() => handleSelectCity(city)}>
                          {city.city}, {city.state}
                        </TableCell>
                        <TableCell align="center" onClick={() => handleSelectCity(city)}>
                          {city.count}
                        </TableCell>
                        <TableCell align="center" onClick={() => handleSelectCity(city)}>
                          {city.avgRisk.toFixed(1)}
                        </TableCell>
                        {selectedLayers.filter(l => l.visible).map(layer => {
                          const layerDef = getLayerById(layer.layerId);
                          if (!layerDef) return <TableCell key={layer.layerId} />;
                          
                          const value = city.layerValues[layerDef.field] || 0;
                          return (
                            <TableCell 
                              key={layer.layerId} 
                              align="center"
                              onClick={() => handleSelectCity(city)}
                            >
                              <Chip 
                                size="small"
                                label={value.toFixed(1)}
                                sx={{ 
                                  bgcolor: `${layerDef.color}25`,
                                  color: layerDef.color,
                                  fontWeight: 'bold'
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectCity(city);
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Add Layer Dialog */}
      <Dialog open={layerDialogOpen} onClose={() => setLayerDialogOpen(false)} maxWidth="md" fullWidth sx={{ zIndex: 10001 }}>
        <DialogTitle>Add Data Layer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select from 37 available data layers across 4 categories
          </Typography>
          
          {['hazard', 'environmental', 'demographic', 'financial'].map(category => {
            const categoryLayers = ALL_LAYERS.filter(l => l.category === category);
            return (
              <Box key={category} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'capitalize', mb: 1 }}>
                  {category} ({categoryLayers.length})
                </Typography>
                <Grid container spacing={1}>
                  {categoryLayers.map(layer => {
                    const isSelected = selectedLayers.find(l => l.layerId === layer.id);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={layer.id}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            border: isSelected ? `2px solid ${layer.color}` : '1px solid #e0e0e0',
                            bgcolor: isSelected ? `${layer.color}15` : 'transparent',
                          }}
                          onClick={() => !isSelected && handleAddLayer(layer.id)}
                        >
                          <CardContent sx={{ py: 1, px: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ color: layer.color }}>{layer.icon}</Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {layer.name}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLayerDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Search Dialog */}
      <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 10001 }}>
        <DialogTitle>Search Locations</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="City or postal code"
            placeholder="Boston MA or 02101"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ mt: 1 }}
            autoFocus
          />
          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={!searchQuery.trim() || searching}
              startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </Box>
          {searchResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Results ({searchResults.length})
              </Typography>
              <List>
                {searchResults.map((result, i) => (
                  <ListItem
                    key={i}
                    button
                    onClick={() => addSearchResult(result)}
                    sx={{ 
                      borderRadius: 1, 
                      mb: 0.5,
                      bgcolor: result.search_type === 'city' ? '#e3f2fd' : 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemIcon>
                      {result.search_type === 'city' ? (
                        <LocationCityIcon color="primary" />
                      ) : (
                        <LocationOnIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        result.search_type === 'city'
                          ? `${result.city}, ${result.state} (${result.locations?.length || 0} postal codes)`
                          : `${result.postal_code} - ${result.city}, ${result.state}`
                      }
                      secondary={`Risk Score: ${(result.risk_score || 0).toFixed(1)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {searchResults.length === 0 && searchQuery && !searching && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {mode === 'discovery' ? (
  <Box>
    No locations found for "{searchQuery}".<br />
    Try searching by city name, state, or ZIP code.
                </Box>
              ) : allLocations.length === 0 ? (
                <Box>
                  <strong>No portfolio loaded.</strong><br />
                  Go to the Portfolio page first and map a portfolio. Then you can search within it.
                </Box>
              ) : (
                <Box>
                  No results found for "{searchQuery}" in your current portfolio ({allLocations.length} locations).<br />
                  <strong>Try searching for:</strong> {Array.from(new Set(allLocations.slice(0, 3).map(l => l.city))).join(', ')}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog (Portfolio Mode) */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 10001 }}>
        <DialogTitle>Advanced Filters</DialogTitle>
        <DialogContent>
          {filters.map((filter, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filter.enabled}
                      onChange={(e) => updateFilter(index, { enabled: e.target.checked })}
                    />
                  }
                  label="Enabled"
                />
                <IconButton size="small" onClick={() => removeFilter(index)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Field"
                    value={filter.field}
                    onChange={(e) => updateFilter(index, { field: e.target.value })}
                  >
                    {ALL_LAYERS.map(layer => (
                      <MenuItem key={layer.id} value={layer.field}>{layer.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Operator"
                    value={filter.operator}
                    onChange={(e) => updateFilter(index, { operator: e.target.value as any })}
                  >
                    <MenuItem value=">=">&gt;=</MenuItem>
                    <MenuItem value="<=">&lt;=</MenuItem>
                    <MenuItem value=">">&gt;</MenuItem>
                    <MenuItem value="<">&lt;</MenuItem>
                    <MenuItem value="=">=</MenuItem>
                    <MenuItem value="between">Between</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={filter.operator === 'between' ? 2.5 : 5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Value"
                    value={filter.value}
                    onChange={(e) => updateFilter(index, { value: parseFloat(e.target.value) })}
                  />
                </Grid>
                {filter.operator === 'between' && (
                  <Grid item xs={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="To"
                      value={filter.value2 || ''}
                      onChange={(e) => updateFilter(index, { value2: parseFloat(e.target.value) })}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addFilter}
          >
            Add Filter
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Scenario Dialog (Portfolio Mode) */}
      <Dialog open={scenarioDialogOpen} onClose={() => setScenarioDialogOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 10001 }}>
        <DialogTitle>Save Scenario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current configuration: {filteredData.length} locations, {selectedLayers.filter(l => l.visible).length} active layers
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={saveScenario}
          >
            Save Current State as Scenario
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScenarioDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Build Portfolio Dialog - Enhanced */}
      <Dialog open={portfolioDialogOpen} onClose={() => setPortfolioDialogOpen(false)} maxWidth="md" fullWidth sx={{ zIndex: 10001 }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New Portfolio</Typography>
            <IconButton onClick={() => setPortfolioDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Portfolio Name"
            value={newPortfolioName}
            onChange={(e) => setNewPortfolioName(e.target.value)}
            sx={{ mt: 1, mb: 3 }}
            autoFocus
            placeholder="e.g., Southeast Regional Properties"
          />
          
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Portfolio Locations ({portfolioItems.length})
          </Typography>
          
          {portfolioItems.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No locations added yet. Click on the map or use the search to add locations to your portfolio.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>City, State</TableCell>
                    <TableCell align="right">Risk Score</TableCell>
                    <TableCell align="right">Asset Value</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolioItems.map((item, index) => (
                    <TableRow key={item.postal_code || index}>
                      <TableCell>{item.postal_code}</TableCell>
                      <TableCell>{item.city}, {item.state}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={(item.risk_score || 0).toFixed(1)} 
                          size="small"
                          color={(item.risk_score || 0) >= 7 ? 'error' : (item.risk_score || 0) >= 4 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={item.asset_value || ''}
                          onChange={(e) => {
                            const newItems = [...portfolioItems];
                            newItems[index] = { ...newItems[index], asset_value: parseFloat(e.target.value) || 0 };
                            setPortfolioItems(newItems);
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          sx={{ width: 120 }}
                          placeholder="0"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            setPortfolioItems(portfolioItems.filter((_, i) => i !== index));
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {portfolioItems.length > 0 && (
            <Box sx={{ display: 'flex', gap: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Locations</Typography>
                <Typography variant="h6">{portfolioItems.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Value</Typography>
                <Typography variant="h6">
                  ${portfolioItems.reduce((sum, item) => sum + (parseFloat(String(item.asset_value)) || 0), 0).toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Avg Risk</Typography>
                <Typography variant="h6">
                  {(portfolioItems.reduce((sum, item) => sum + (item.risk_score || 0), 0) / portfolioItems.length).toFixed(1)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={saveBuildPortfolio}
            disabled={!newPortfolioName.trim() || portfolioItems.length === 0}
            startIcon={<SaveIcon />}
          >
            Save Portfolio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Detail Dialog - Click to Explore */}
      <Dialog 
        open={locationDetailOpen} 
        onClose={() => setLocationDetailOpen(false)} 
        maxWidth="md" 
        fullWidth
        sx={{ zIndex: 10001 }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon color="primary" />
              <Typography variant="h6">
                {loadingLocation ? 'Loading...' : clickedLocation ? `${clickedLocation.postal_code} - ${clickedLocation.city}, ${clickedLocation.state}` : 'Location Details'}
              </Typography>
            </Box>
            <IconButton onClick={() => setLocationDetailOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingLocation ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : clickedLocation ? (
            <Box>
              {/* Location Header */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Risk Score</Typography>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 'bold',
                    color: (clickedLocation.risk_score || 0) >= 7 ? 'error.main' : 
                           (clickedLocation.risk_score || 0) >= 4 ? 'warning.main' : 'success.main'
                  }}>
                    {(clickedLocation.risk_score || 0).toFixed(1)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Population</Typography>
                  <Typography variant="h5">{(clickedLocation.population || 0).toLocaleString()}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Building Value</Typography>
                  <Typography variant="h5">${((clickedLocation.building_value || 0) / 1000000).toFixed(1)}M</Typography>
                </Box>
              </Box>

              {/* Climate Hazards */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Climate Hazards (18 Types)</Typography>
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {[
                  { name: 'Hurricane', field: 'hurricane', color: '#9C27B0' },
                  { name: 'Coastal Flood', field: 'flood', color: '#2196F3' },
                  { name: 'River Flood', field: 'flood_r', color: '#03A9F4' },
                  { name: 'Wildfire', field: 'wildfire', color: '#FF5722' },
                  { name: 'Earthquake', field: 'earthquake', color: '#795548' },
                  { name: 'Drought', field: 'drought', color: '#FFC107' },
                  { name: 'Heat', field: 'heat', color: '#FF9800' },
                  { name: 'Cold', field: 'cold', color: '#00BCD4' },
                  { name: 'Tornado', field: 'tornado', color: '#E91E63' },
                  { name: 'Wind', field: 'wind', color: '#9E9E9E' },
                  { name: 'Hail', field: 'hail', color: '#607D8B' },
                  { name: 'Ice Storm', field: 'ice', color: '#00E5FF' },
                  { name: 'Winter Storm', field: 'winter', color: '#B3E5FC' },
                  { name: 'Lightning', field: 'lightning', color: '#FFEB3B' },
                  { name: 'Landslide', field: 'landslide', color: '#A1887F' },
                  { name: 'Tsunami', field: 'tsunami', color: '#006064' },
                  { name: 'Volcanic', field: 'volcanic', color: '#BF360C' },
                  { name: 'Avalanche', field: 'avalanche', color: '#ECEFF1' },
                ].map(hazard => {
                  const value = (clickedLocation as any)[hazard.field] || 0;
                  return (
                    <Grid item xs={6} sm={4} md={3} key={hazard.field}>
                      <Paper sx={{ p: 1.5, borderLeft: `4px solid ${hazard.color}` }}>
                        <Typography variant="caption" color="text.secondary">{hazard.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: value >= 5 ? 'error.main' : 'text.primary' }}>
                            {value.toFixed(1)}
                          </Typography>
                          {value >= 5 && <WarningIcon color="error" fontSize="small" />}
                        </Box>
                        <Box sx={{ height: 4, bgcolor: '#e0e0e0', borderRadius: 2, mt: 0.5 }}>
                          <Box sx={{ height: '100%', width: `${Math.min(100, value * 10)}%`, bgcolor: hazard.color, borderRadius: 2 }} />
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Community Metrics */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Community Metrics</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Resilience</Typography>
                    <Typography variant="h5">{(clickedLocation.resilience || 0).toFixed(1)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Community Risk</Typography>
                    <Typography variant="h5">{(clickedLocation.community_risk || 0).toFixed(1)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Superfund Sites</Typography>
                    <Typography variant="h5">{clickedLocation.superfund_sites || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">EPA Facilities</Typography>
                    <Typography variant="h5">{clickedLocation.epa_facilities || 0}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Add to Portfolio Section */}
              {buildMode && (
                <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '2px dashed', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>Add to Portfolio</Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <TextField
                      label="Asset Value (optional)"
                      type="number"
                      value={addToPortfolioValue}
                      onChange={(e) => setAddToPortfolioValue(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      sx={{ flex: 1 }}
                      placeholder="0"
                    />
                    <Button 
                      variant="contained"
                      onClick={() => {
                        if (clickedLocation && !portfolioItems.find(p => p.postal_code === clickedLocation.postal_code)) {
                          setPortfolioItems([...portfolioItems, {
                            ...clickedLocation,
                            asset_value: parseFloat(addToPortfolioValue) || 0
                          }]);
                          setAddToPortfolioValue('');
                          setLocationDetailOpen(false);
                        }
                      }}
                      disabled={portfolioItems.some(p => p.postal_code === clickedLocation.postal_code)}
                      startIcon={<AddIcon />}
                    >
                      {portfolioItems.some(p => p.postal_code === clickedLocation.postal_code) ? 'Already Added' : 'Add to Portfolio'}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              No location data found for this area. Try clicking closer to a populated area.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {clickedLocation && !buildMode && (
            <Button 
              variant="outlined" 
              onClick={() => {
                setBuildMode(true);
                // Optionally add this location immediately
              }}
              startIcon={<BuildIcon />}
            >
              Start Building Portfolio
            </Button>
          )}
          <Button onClick={() => setLocationDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CombinedRiskMap;